import * as THREE from 'three';
import './ui/styles.css';

import {
  state,
  notify,
  subscribe,
  aspectRatioValue,
  exportDimensions,
  frameToSeconds,
  clipDurationSeconds,
  type Fps,
  type ViewMode,
  type AspectKey,
  type ResolutionKey,
  type VideoLayout,
} from './state.ts';
import { createViewport, type RenderRegion } from './scene/viewport.ts';
import { OctahedronBoneHelper } from './scene/boneOcta.ts';
import { createEnvironment } from './scene/environment.ts';
import { createRenderLoop } from './scene/renderLoop.ts';
import { createVideoOverlay } from './scene/videoOverlay.ts';
import { createTimeline } from './timeline/timeline.ts';
import { createDropzone, type DroppedFile } from './io/dropzone.ts';
import { parseMocapFbx, buildItemClips, type LoadedMocap, type TakeItem } from './io/loadFbx.ts';
import { parseTargetMesh } from './io/loadMesh.ts';
import { parseVrm, disposeVrm } from './io/loadVrm.ts';
import type { VRM } from '@pixiv/three-vrm';
import { disposeObject } from './io/dispose.ts';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import {
  createRetargeter,
  captureBindPose,
  toCanonical,
  evalAdjustKeys,
  type ActorAdjust,
  type AdjustKey,
  type Retargeter,
  type SourceBindPose,
} from './retarget/retarget.ts';
import { createBoneMapModal } from './ui/boneMapModal.ts';
import {
  createContactSolver,
  evalInfluence,
  LIMB_CHAINS,
  LIMB_LABELS,
  sanitizeContacts,
  type ContactLimb,
  type ContactPin,
} from './retarget/contacts.ts';
import { createTimelineTracks, type Lane } from './ui/timelineTracks.ts';
import { createContactsPanel } from './ui/contactsPanel.ts';
import { createFingerHealer, type FingerHealer } from './retarget/fingers.ts';
import {
  createFingerTuner,
  defaultFingerTune,
  evalFingerTuneKeys,
  FINGER_TUNE_FIELDS,
  isFingerTuneActive,
  sanitizeFingerTune,
  type FingerTune,
  type FingerTuneKey,
  type FingerTuner,
} from './retarget/fingerTune.ts';
import { createEditGizmos, type OffsetAnchor } from './ui/editGizmos.ts';
import { createAgentApi, type SceneSnapshot } from './agent/api.ts';
import { createAnalyzer } from './agent/analyze.ts';
import { createAssistant } from './agent/assistant.ts';
import { createAssistantPanel } from './ui/assistantPanel.ts';
import { createCameraDirector } from './camera/director.ts';
import { createCameraViz } from './camera/viz.ts';
import {
  findCamera,
  nextCameraColor,
  nextCameraName,
  normalizeShots,
  sanitizeProgram,
  sortKeyframes,
  type CameraDef,
  type CameraKeyframe,
  type CamerasFile,
  type Vec3,
} from './camera/types.ts';
import { defaultParams, getPreset, type PresetContext } from './camera/presets.ts';
import { createCameraPanel } from './ui/cameraPanel.ts';
import { createCameraStrip } from './ui/cameraStrip.ts';
import { createSidebar, type FileMeta } from './ui/sidebar.ts';
import { createControlbar } from './ui/controlbar.ts';
import { bakeAndExportGlb } from './export/exportGlb.ts';
import { exportVideo } from './export/exportVideo.ts';
import type { SceneFile, ScenePuppet } from './io/scene.ts';

// --- Scene + loop --------------------------------------------------------------
const canvas = document.getElementById('webgl') as HTMLCanvasElement;
const viewport = createViewport(canvas);
const environment = createEnvironment(viewport);
// Aim the key light at the default angles so sliders and light agree from the start.
environment.setLightAngles(state.lighting.azimuth, state.lighting.elevation);
environment.setShadowOpacity(state.lighting.shadowOpacity);
/** Push the current playhead to the reference video (seek / play in sync). */
function syncVideo(): void {
  if (videoOverlay.hasVideo()) {
    videoOverlay.sync(state.currentFrame, state.fps, state.playing, state.video.frameDelta);
  }
}
const videoOverlay = createVideoOverlay(canvas, syncVideo, (msg) => showError(msg));

/** In a split layout, render the 3D into the half the video doesn't occupy. */
function videoRegionFor(layout: VideoLayout): RenderRegion | null {
  if (!videoOverlay.hasVideo()) return null;
  if (layout === 'split-lr') return { x: 0, y: 0, w: 0.5, h: 1 }; // 3D = left half
  if (layout === 'split-tb') return { x: 0, y: 0, w: 1, h: 0.5 }; // 3D = top half
  return null;
}

function applyVideoViewportRegion(): void {
  viewport.setRenderRegion(videoRegionFor(state.video.layout));
  loop.renderOnce();
}

/** One take item (actor skeleton or prop) plus its viz helper and optional puppet. */
interface ActorEntry {
  item: TakeItem;
  helper: THREE.SkeletonHelper | THREE.AxesHelper | null;
  /** Octahedral bone viz for 'octa' view mode (skeleton items only). */
  octa: OctahedronBoneHelper | null;
  puppet: THREE.Object3D | null;
  retargeter: Retargeter | null;
  /** Set when the puppet is a per-actor VRM avatar (drives springs, disposal). */
  vrm: VRM | null;
  avatarName: string | null;
  /** Explicit canonical→bone target map (VRM humanoid); null = match by name. */
  targetMap: Map<string, THREE.Object3D> | null;
  /** User remaps from the bone-mapping UI: canonical slot → source bone name. */
  boneOverrides: Map<string, string> | null;
  /** "Adjust Actor Offset": world offset + size, mutated in place (live ref). */
  adjust: ActorAdjust;
  /** Keyframed adjustments; when non-empty they drive `adjust` every tick. */
  adjustKeys: AdjustKey[];
  /** Per-avatar finger offsets (VRM 0.x thumb fix etc.), live values. */
  fingerTune: FingerTune;
  /** Keyframed finger tunes; when non-empty they drive `fingerTune` every tick. */
  fingerTuneKeys: FingerTuneKey[];
  /** Applies fingerTune to the target rig; rebuilt on avatar install. */
  fingerTuner: FingerTuner | null;
  /** The puppet root's own scale at install time (adjust.scale multiplies it). */
  puppetBaseScale: number;
  visible: boolean;
  color: number | null;
}
let actors: ActorEntry[] = [];
let sourceBind: SourceBindPose | null = null;
let fingerHealer: FingerHealer | null = null;

/** Where the currently-loaded mocap / puppet came from — retained for scene save. */
type PuppetSource =
  | { type: 'baked'; id: 'xbot' | 'ybot' }
  | { type: 'custom'; name: string; ext: string; buffer: ArrayBuffer };
let sourceFile: { name: string; ext: string; buffer: ArrayBuffer } | null = null;
let currentPuppetSource: PuppetSource | null = null;
/** True while an MP4 export is compositing the reference video into frames. */
let videoCompositeExport = false;

/** Distinct default colours so each actor is differentiable out of the box. */
const ACTOR_PALETTE = [0x4f9cff, 0xff6b6b, 0x5dd56d, 0xffb347, 0xb98cff, 0x36d0d0, 0xff7fd0];

/** Clock for VRM spring-bone physics; exports use a fixed 1/fps step instead. */
const vrmClock = new THREE.Clock();

/**
 * Per-tick actor pipeline (three passes so cross-actor contacts see final
 * poses): 1) retarget every puppet, 2) contact-pin IK, 3) VRM spring bones.
 * Exports run this same function, so every adjustment bakes for free.
 */
/** Drive each actor's live adjust/finger values from its keys at time t (seconds). */
function evalActorAdjustsAt(seconds: number): void {
  for (const a of actors) {
    if (a.adjustKeys.length > 0) {
      evalAdjustKeys(a.adjustKeys, seconds, a.adjust);
      if (a.puppet) a.puppet.scale.setScalar(a.puppetBaseScale * a.adjust.scale);
    }
    if (a.fingerTuneKeys.length > 0) {
      evalFingerTuneKeys(a.fingerTuneKeys, seconds, a.fingerTune);
    }
  }
}

function tickActors(): void {
  const mesh = state.viewMode === 'mesh';
  const dt = state.exporting ? 1 / state.fps : Math.min(vrmClock.getDelta(), 0.1);
  // Keyframed offsets evaluate first so the retargeters read this frame's values.
  evalActorAdjustsAt(state.mixer?.time ?? 0);
  // Finger healing runs on the SOURCE bones first, so the skeleton view, every
  // retargeted avatar and all exports see the cleaned hands.
  if (state.fingerFix > 0) fingerHealer?.apply(state.fingerFix);
  for (const a of actors) {
    if (mesh && a.retargeter && a.puppet) {
      a.retargeter.apply();
      // Per-avatar finger offsets (VRM 0.x thumb fix) ride on top of the retarget.
      if (a.fingerTuner && isFingerTuneActive(a.fingerTune)) a.fingerTuner.apply(a.fingerTune);
    }
    if (a.item.kind === 'prop' && a.helper) {
      const bone = a.item.bones[0];
      if (bone) {
        bone.updateWorldMatrix(true, false);
        a.helper.position.setFromMatrixPosition(bone.matrixWorld);
      }
    }
  }
  if (state.contacts.length > 0) applyAllContacts(state.mixer?.time ?? 0, mesh);
  for (const a of actors) {
    // Spring bones (hair/cloth) — autoUpdateHumanBones is off, so this never
    // overwrites the retargeted/pinned pose.
    if (mesh && a.retargeter && a.puppet) a.vrm?.update(dt);
  }
}

/** Puppet bone filling a humanoid slot on this actor (from the retarget pairs). */
function puppetBoneOf(a: ActorEntry, canonical: string): THREE.Object3D | null {
  return a.retargeter?.pairs.find((p) => p.canonical === canonical)?.target ?? null;
}

/** Source-skeleton bone for a humanoid slot (honours bone-mapping overrides). */
function sourceBoneOf(a: ActorEntry, canonical: string): THREE.Object3D | null {
  const overrideName = a.boneOverrides?.get(canonical);
  if (overrideName !== undefined) {
    return overrideName ? (a.item.bones.find((b) => b.name === overrideName) ?? null) : null;
  }
  return a.item.bones.find((b) => toCanonical(b.name) === canonical) ?? null;
}

/** The bone a contact pin should drive: puppet in mesh view, else the source. */
function chainBoneOf(a: ActorEntry, canonical: string, mesh: boolean): THREE.Object3D | null {
  if (mesh && a.retargeter && a.puppet) return puppetBoneOf(a, canonical);
  return sourceBoneOf(a, canonical);
}

/**
 * An actor's bone for a canonical slot, used to resolve a pin's TARGET position.
 * `chainBoneOf` (which drives the pinned LIMB) only exposes bones the retargeter
 * MATCHED — the source∩target intersection — so optional target bones like the
 * shoulders (many avatar/humanoid rigs omit them) resolve to null there and the
 * whole pin gets skipped (`if (!bone) continue`). For a pure target lookup we
 * want the actor's full rig instead: the VRM humanoid map, then any matched pair,
 * then the source skeleton — which always carries every bone and is always posed.
 */
function actorBoneObj(actorId: string, canonical: string, mesh: boolean): THREE.Object3D | null {
  const a = actors.find((x) => x.item.id === actorId);
  if (!a) return null;
  if (mesh && a.puppet) {
    const puppetBone = a.targetMap?.get(canonical) ?? puppetBoneOf(a, canonical);
    if (puppetBone) return puppetBone;
  }
  return sourceBoneOf(a, canonical);
}

const contactTargetScratch = new THREE.Vector3();
const contactOffsetScratch = new THREE.Vector3();
const contactBoneQuat = new THREE.Quaternion();
const contactSolver = createContactSolver();

/**
 * Solve every active contact pin (runs after all retargeters each tick). In
 * mesh view pins drive the puppet limb chains; otherwise the source skeleton
 * (so bare stick figures pin too — the stateful solver keeps that idempotent).
 */
function applyAllContacts(seconds: number, mesh: boolean): void {
  for (const pin of state.contacts) {
    if (!pin.enabled) continue;
    const weight = evalInfluence(pin.keys, seconds);
    if (weight <= 0.001) continue;
    const a = actors.find((x) => x.item.id === pin.actorId);
    if (!a) continue;
    const [upperKey, lowerKey, endKey] = LIMB_CHAINS[pin.limb];
    const upper = chainBoneOf(a, upperKey, mesh);
    const lower = chainBoneOf(a, lowerKey, mesh);
    const end = chainBoneOf(a, endKey, mesh);
    if (!upper || !lower || !end) continue;

    let target: THREE.Vector3 | null = null;
    let orient: { targetBone: THREE.Object3D; rotate: [number, number, number] } | null = null;
    if (pin.target.kind === 'point') {
      target = contactTargetScratch.fromArray(pin.target.point);
    } else {
      const bone = actorBoneObj(pin.target.actorId, pin.target.bone, mesh);
      if (!bone) continue;
      bone.updateWorldMatrix(true, false);
      target = contactTargetScratch.setFromMatrixPosition(bone.matrixWorld);
      // Offset rides the bone's LOCAL frame ("5cm up the wrist" stays put).
      if (pin.target.offset) {
        bone.getWorldQuaternion(contactBoneQuat);
        target.add(contactOffsetScratch.fromArray(pin.target.offset).applyQuaternion(contactBoneQuat));
      }
      if (pin.target.matchRotation) {
        orient = { targetBone: bone, rotate: pin.target.rotate ?? [0, 0, 0] };
      }
    }
    if (!target) continue;
    contactSolver.solve(pin.id, upper, lower, end, target, weight, orient);
  }
}

function setMixersTime(seconds: number): void {
  for (const m of state.mixers) m.setTime(seconds);
}

// Drives the viewport camera from the camera program; in Free view it just
// runs OrbitControls (see renderLoop's updateCamera hook).
const director = createCameraDirector(viewport, {
  getAimActors: () =>
    actors.map((a) => ({
      id: a.item.id,
      kind: a.item.kind,
      bones: a.item.bones,
      hips: a.item.hips,
    })),
  getSeconds: () => state.mixer?.time ?? 0,
  onApplied: () => updateCameraReadout(),
});

// In-viewport camera path/waypoint/frustum viz (Free view only).
const cameraViz = createCameraViz(viewport, {
  onSelectCamera: (id) => selectCamera(id),
  onSelectKeyframe: (index) => selectKeyframe(index),
  onMoveKeyframe: (cameraId, index, position) => moveKeyframePosition(cameraId, index, position),
  onSelectAimPoint: () => {
    state.selectedKeyframe = null;
    notify();
  },
  onMoveAimPoint: (cameraId, position) => moveAimPoint(cameraId, position),
});

const loop = createRenderLoop(
  viewport,
  () => notify(), // onFrame: timeline follows playback
  () => {
    tickActors(); // keep puppets + prop markers in sync
    cameraViz.tick(state.mixer?.time ?? 0); // pose ghost cameras at the playhead
    editGizmos.tick(); // markers/deviation lines follow the animated hips
  },
  director.tick // camera: orbit controls in Free view, program otherwise
);
const timeline = createTimeline(loop);

// --- UI ------------------------------------------------------------------------
const sidebar = createSidebar({
  onToggleGrid: (v) => environment.toggleGrid(v),
  onGroundShadow: (on) => {
    state.lighting.shadow = on;
    environment.setGroundShadow(on);
    loop.renderOnce();
    notify();
  },
  onShadowOpacity: (opacity) => {
    state.lighting.shadowOpacity = opacity;
    environment.setShadowOpacity(opacity);
    loop.renderOnce();
  },
  onAdjustSelect: () => notify(), // gizmos re-read the adjust selection
  onVideoExportComposite: (on) => {
    state.video.includeInExport = on;
    notify();
  },
  onLightAngles: (azimuth, elevation) => {
    state.lighting.azimuth = azimuth;
    state.lighting.elevation = elevation;
    environment.setLightAngles(azimuth, elevation);
    loop.renderOnce();
  },
  onMatchProportions: (on) => setMatchProportions(on),
  onFov: (fov) => environment.updateCameraFOV(fov),
  onFps: (fps: Fps) => timeline.setFps(fps),
  onViewMode: (mode: ViewMode) => setViewMode(mode),
  onToggleActor: (id, visible) => toggleActorVisible(id, visible),
  onActorColor: (id, hex) => setActorColor(id, parseInt(hex.slice(1), 16)),
  onLoadAvatar: (id) => {
    pendingAvatarActor = id;
    avatarFileInput.click();
  },
  onMapBones: (id) => openBoneMapFor(id),
  onAdjustChange: (id, patch) => setActorAdjust(id, patch),
  onAdjustReset: (id) => resetActorAdjust(id),
  onAdjustKey: (id) => setAdjustKeyAtPlayhead(id),
  onAdjustKeyDelete: (id, index) => deleteAdjustKey(id, index),
  onFingerTune: (id, patch) => setFingerTune(id, patch),
  onFingerKey: (id) => setFingerKeyAtPlayhead(id),
  getFingerTuneLive: (id) => {
    const a = actors.find((x) => x.item.id === id);
    return a ? { ...a.fingerTune } : null;
  },
  getAdjustLive: (id) => {
    const a = actors.find((x) => x.item.id === id);
    if (!a) return null;
    return {
      x: a.adjust.offset[0],
      y: a.adjust.offset[1],
      z: a.adjust.offset[2],
      scale: a.adjust.scale,
    };
  },
  onFingerFix: (strength) => {
    state.fingerFix = strength;
    tickActors();
    loop.renderOnce();
    notify();
  },
  onPreviewMesh: (which) => void loadBakedPuppet(which),
  onBackground: (hex) => setBackground(parseInt(hex.slice(1), 16)),
  onAspect: (key) => setAspect(key),
  onResolution: (key) => setResolution(key),
  onSaveScene: () => saveScene(),
  onLoadScene: (file) => void loadScene(file),
  onVideoLayout: (layout) => {
    state.video.layout = layout;
    videoOverlay.setLayout(layout);
    videoOverlay.setVideoOpacity(state.video.opacity);
    videoOverlay.setSceneOpacity(state.video.sceneOpacity);
    notify(); // recomputes the transparent-background composite
    applyVideoViewportRegion();
    notify();
  },
  onVideoDelta: (delta) => {
    state.video.frameDelta = delta;
    syncVideo();
    notify();
  },
  onVideoOpacity: (o) => {
    state.video.opacity = o;
    videoOverlay.setVideoOpacity(o);
  },
  onSceneOpacity: (o) => {
    state.video.sceneOpacity = o;
    videoOverlay.setSceneOpacity(o);
  },
  onVideoOnTop: (onTop) => {
    state.video.onTop = onTop;
    videoOverlay.setOnTop(onTop);
    notify(); // recomputes the transparent-background composite
  },
  onVideoKeyBg: (keyBackground) => {
    state.video.keyBackground = keyBackground;
    notify();
  },
  onRemoveVideo: () => removeVideo(),
});

// --- Camera program actions ------------------------------------------------------

/**
 * Context a preset bakes against: the base pose (current free view, or the
 * camera's first keyframe on re-bake) plus the subject anchor and marked range.
 */
function buildPresetContext(subjectActorId: string | null, base?: CameraDef): PresetContext {
  let position: THREE.Vector3;
  let target: THREE.Vector3;
  let fov: number;
  if (base) {
    const k = base.keyframes[0];
    position = new THREE.Vector3().fromArray(k.position);
    target = new THREE.Vector3().fromArray(k.target);
    fov = k.fov;
  } else {
    position = viewport.camera.position.clone();
    fov = viewport.camera.fov;
    target =
      state.cameraView === 'free'
        ? viewport.controls.target.clone()
        : new THREE.Vector3(0, 0, -3).applyQuaternion(viewport.camera.quaternion).add(position);
  }

  let subject = target.clone();
  if (subjectActorId) {
    const hips = director.resolveAim({
      mode: 'bone',
      actorId: subjectActorId,
      bone: 'hips',
      heightOffset: 0,
    });
    if (hips) subject = hips.clone();
  }

  const startSec = frameToSeconds(state.startFrame);
  const endSec = Math.max(frameToSeconds(state.endFrame), startSec + 0.5);
  return {
    position,
    target,
    fov,
    subject,
    subjectActorId,
    startSec,
    endSec,
    // Scrub the mixers to sample the subject's path (follow preset), then restore.
    sampleSubject: (sec) => {
      if (!subjectActorId || !state.mixer) return null;
      const saved = state.mixer.time;
      setMixersTime(sec);
      const p = director.resolveAim({
        mode: 'bone',
        actorId: subjectActorId,
        bone: 'hips',
        heightOffset: 0,
      });
      const result = p ? p.clone() : null;
      setMixersTime(saved);
      return result;
    },
  };
}

/** Instantiate a preset as a new camera spanning the marked range. */
function addCameraFromPreset(presetId: string, subjectActorId: string | null): void {
  const preset = getPreset(presetId);
  if (!preset) return;
  const ctx = buildPresetContext(subjectActorId);
  const params = defaultParams(preset, ctx);
  const result = preset.generate(ctx, params);
  const def: CameraDef = {
    id: crypto.randomUUID(),
    name: nextCameraName(state.cameras),
    color: nextCameraColor(state.cameras),
    interp: result.interp,
    keyframes: result.keyframes,
    aim: result.aim,
    shake: result.shake,
    preset: { id: presetId, params, subjectActorId },
  };
  state.cameras.cameras.push(def);
  state.selectedCameraId = def.id;
  state.selectedKeyframe = null;
  director.setView(def.id); // look through it for instant feedback
  notify();
  if (presetId === 'tiktok' && state.aspect !== '9:16') {
    showToast(`${def.name} added — tip: switch Aspect ratio to 9:16 for vertical framing.`);
  } else {
    showToast(`${def.name} added (${preset.label}).`);
  }
}

/** A preset slider changed: store the value and re-bake from the first keyframe. */
function setPresetParam(cameraId: string, paramKey: string, value: number): void {
  const def = findCamera(state.cameras, cameraId);
  const preset = def?.preset ? getPreset(def.preset.id) : null;
  if (!def || !def.preset || !preset) return;
  def.preset.params[paramKey] = value;
  const ctx = buildPresetContext(def.preset.subjectActorId, def);
  const result = preset.generate(ctx, def.preset.params);
  def.keyframes = result.keyframes;
  def.aim = result.aim;
  def.interp = result.interp;
  // The user's shake settings survive a re-bake; only the path is regenerated.
  notify();
}

/** Shake Amount slider (0–100) ↔ posAmp/rotAmp mapping. */
const SHAKE_MAX_POS = 0.12; // metres at 100
const SHAKE_MAX_ROT = 4; // degrees at 100

function setCameraShake(
  cameraId: string,
  patch: { enabled?: boolean; amount?: number; freq?: number }
): void {
  const def = findCamera(state.cameras, cameraId);
  if (!def) return;
  if (!def.shake) {
    def.shake = { enabled: false, posAmp: 0.05, rotAmp: 1.6, freq: 1.8, seed: 1 };
  }
  if (patch.enabled !== undefined) def.shake.enabled = patch.enabled;
  if (patch.amount !== undefined) {
    def.shake.posAmp = (patch.amount / 100) * SHAKE_MAX_POS;
    def.shake.rotAmp = (patch.amount / 100) * SHAKE_MAX_ROT;
  }
  if (patch.freq !== undefined) def.shake.freq = patch.freq;
  notify();
}

// --- Cameras file (standalone *.cameras.json) -----------------------------------------

/** Download the camera program on its own, reusable in other scenes. */
function exportCameras(): void {
  if (state.cameras.cameras.length === 0) {
    showError('No cameras to export.');
    return;
  }
  const payload: CamerasFile = {
    app: 'aka-mocap-viewer',
    kind: 'cameras',
    version: 1,
    program: state.cameras,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const base = (sourceFile?.name ?? 'take').replace(/\.[^.]+$/, '');
  downloadBlob(blob, `${base}.cameras.json`);
  showToast('Cameras exported.');
}

/**
 * Import a camera program. Aim bindings to actors that don't exist in this
 * take are remapped by skeleton number (Skeleton 002 → this take's second
 * skeleton). With no cameras yet the whole program (including cuts) is
 * adopted; otherwise the cameras are appended and imported cuts are dropped.
 */
function importCamerasProgram(raw: unknown): void {
  if (state.totalFrames === 0) {
    showError('Load a mocap take before importing cameras.');
    return;
  }
  const parsed = raw as Partial<CamerasFile>;
  const program = sanitizeProgram(parsed?.kind === 'cameras' ? parsed.program : raw);
  if (!program || program.cameras.length === 0) {
    showError('No cameras found in that file.');
    return;
  }

  const skeletons = actors.filter((a) => a.item.kind === 'skeleton').map((a) => a.item.id);
  let remapped = 0;
  for (const cam of program.cameras) {
    if (cam.aim.mode !== 'bone' || skeletons.includes(cam.aim.actorId)) continue;
    const n = parseInt(/(\d+)$/.exec(cam.aim.actorId)?.[1] ?? '1', 10);
    const target = skeletons[Math.min(Math.max(n - 1, 0), skeletons.length - 1)];
    if (target) {
      cam.aim.actorId = target;
      remapped++;
    } else {
      cam.aim = { mode: 'path' }; // no skeletons at all — fall back to the path
      remapped++;
    }
  }

  // Fresh ids so importing the same file twice can't collide.
  const idMap = new Map(program.cameras.map((c) => [c.id, crypto.randomUUID()]));
  for (const c of program.cameras) c.id = idMap.get(c.id)!;
  for (const s of program.shots) s.cameraId = idMap.get(s.cameraId)!;

  const count = program.cameras.length;
  if (state.cameras.cameras.length === 0) {
    state.cameras = program;
  } else {
    state.cameras.cameras.push(...program.cameras);
  }
  state.selectedCameraId = program.cameras[0].id;
  state.selectedKeyframe = null;
  notify();
  showToast(
    `Imported ${count} camera(s)` +
      (remapped > 0 ? `, ${remapped} retargeted to this take's actors.` : '.')
  );
}

function importCamerasFile(file: File): void {
  file
    .text()
    .then((text) => importCamerasProgram(JSON.parse(text)))
    .catch((err) => showError(`Couldn't read cameras: ${(err as Error).message}`));
}

// --- Camera aim (tracking targets) ----------------------------------------------------

/** Change what a camera looks at: keyframed path, actor bone, point or group. */
function setCameraAim(
  cameraId: string,
  mode: 'path' | 'bone' | 'point' | 'group',
  opts?: { actorId?: string; bone?: string; heightOffset?: number }
): void {
  const def = findCamera(state.cameras, cameraId);
  if (!def) return;
  if (mode === 'path') {
    def.aim = { mode: 'path' };
  } else if (mode === 'group') {
    def.aim = { mode: 'group', actorIds: 'all' };
  } else if (mode === 'bone') {
    const prev = def.aim.mode === 'bone' ? def.aim : null;
    const firstSkeleton = actors.find((a) => a.item.kind === 'skeleton')?.item.id ?? '';
    def.aim = {
      mode: 'bone',
      actorId: opts?.actorId ?? prev?.actorId ?? firstSkeleton,
      bone: opts?.bone ?? prev?.bone ?? 'hips',
      heightOffset: opts?.heightOffset ?? prev?.heightOffset ?? 0,
    };
  } else if (def.aim.mode !== 'point') {
    // Start the fixed point at wherever the camera is currently looking.
    const sample = director.sample(def.id, state.mixer?.time ?? 0);
    const point = (sample?.target ?? viewport.controls.target).toArray() as Vec3;
    def.aim = { mode: 'point', point };
  }
  notify();
}

/** The viewport aim marker moved (fixed-point aim). */
function moveAimPoint(cameraId: string, position: THREE.Vector3): void {
  const def = findCamera(state.cameras, cameraId);
  if (!def || def.aim.mode !== 'point') return;
  def.aim.point = position.toArray() as Vec3;
  notify();
}

// --- Camera keyframes ---------------------------------------------------------------

/** Add/update a keyframe at the playhead from the current Free view (K). */
function setKeyframeFromView(): void {
  const def = findCamera(state.cameras, state.selectedCameraId);
  if (!def) {
    showError('Select a camera first.');
    return;
  }
  if (state.cameraView !== 'free') {
    showError('Switch to Free view to frame a keyframe.');
    return;
  }
  const t = frameToSeconds(state.currentFrame);
  const existing = def.keyframes.findIndex((k) => Math.abs(k.time - t) < 0.5 / state.fps);
  const key = {
    time: t,
    position: viewport.camera.position.toArray() as Vec3,
    target: viewport.controls.target.toArray() as Vec3,
    fov: viewport.camera.fov,
    easing: existing >= 0 ? def.keyframes[existing].easing : ('ease' as const),
  };
  if (existing >= 0) def.keyframes[existing] = key;
  else def.keyframes.push(key);
  sortKeyframes(def);
  state.selectedKeyframe = def.keyframes.indexOf(key);
  notify();
  showToast(existing >= 0 ? 'Keyframe updated.' : `Keyframe added to ${def.name}.`);
}

/** Delete the selected keyframe (a camera always keeps at least one). */
function deleteSelectedKeyframe(): boolean {
  const def = findCamera(state.cameras, state.selectedCameraId);
  const index = state.selectedKeyframe;
  if (!def || index === null || !def.keyframes[index]) return false;
  if (def.keyframes.length <= 1) {
    showToast('A camera needs at least one keyframe.');
    return true;
  }
  def.keyframes.splice(index, 1);
  state.selectedKeyframe = null;
  notify();
  return true;
}

/** Select a keyframe and scrub the playhead to it. */
function selectKeyframe(index: number): void {
  const def = findCamera(state.cameras, state.selectedCameraId);
  const key = def?.keyframes[index];
  if (!def || !key) return;
  state.selectedKeyframe = index;
  state.selectedContactId = null; // one live gizmo: camera keyframe wins now
  timeline.scrubToFrame(Math.round(key.time * state.fps));
}

/** Retime a keyframe from the strip diamond drag (frame-snapped, clamped). */
function moveKeyframeTime(index: number, seconds: number): void {
  const def = findCamera(state.cameras, state.selectedCameraId);
  const key = def?.keyframes[index];
  if (!def || !key) return;
  const fps = state.fps;
  const min = index > 0 ? def.keyframes[index - 1].time + 1 / fps : 0;
  const max =
    index + 1 < def.keyframes.length
      ? def.keyframes[index + 1].time - 1 / fps
      : clipDurationSeconds();
  key.time = Math.min(Math.max(Math.round(seconds * fps) / fps, min), Math.max(min, max));
  notify();
}

/** Write a waypoint gizmo position back into its keyframe. */
function moveKeyframePosition(cameraId: string, index: number, position: THREE.Vector3): void {
  const def = findCamera(state.cameras, cameraId);
  const key = def?.keyframes[index];
  if (!key) return;
  key.position = position.toArray() as Vec3;
  notify();
}

function setKeyframeEasing(easing: string): void {
  const def = findCamera(state.cameras, state.selectedCameraId);
  const key = state.selectedKeyframe !== null ? def?.keyframes[state.selectedKeyframe] : null;
  if (!key) return;
  key.easing = easing as CameraKeyframe['easing'];
  notify();
}

// --- Camera cuts -------------------------------------------------------------------

/** The camera showing at `sec` in the program (cut lookup with fallback). */
function programCameraAt(sec: number): CameraDef | null {
  const shots = state.cameras.shots;
  let active = shots.length > 0 ? shots[0] : null;
  for (const s of shots) {
    if (s.time <= sec) active = s;
    else break;
  }
  return active
    ? (findCamera(state.cameras, active.cameraId) ?? state.cameras.cameras[0] ?? null)
    : (state.cameras.cameras[0] ?? null);
}

/**
 * Razor at the playhead: the new shot defaults to the next camera in the list
 * (cycling), so repeated cuts alternate cameras with no extra clicks.
 */
function addCutAtPlayhead(): void {
  const cams = state.cameras.cameras;
  if (cams.length === 0 || state.totalFrames === 0) {
    showError('Add a camera before cutting.');
    return;
  }
  const sec = frameToSeconds(state.currentFrame);
  const shots = state.cameras.shots;
  if (shots.some((s) => Math.abs(s.time - sec) < 1e-6) || (shots.length === 0 && sec === 0)) {
    if (shots.length === 0) {
      // Cutting at frame 0 with no cuts yet just starts the program explicitly.
      const first = findCamera(state.cameras, state.selectedCameraId) ?? cams[0];
      shots.push({ time: 0, cameraId: first.id });
    } else {
      showToast('There is already a cut at this frame.');
      return;
    }
  } else if (shots.length === 0) {
    // First cut mid-clip: anchor the program at 0, then cut to the next camera.
    const first = findCamera(state.cameras, state.selectedCameraId) ?? cams[0];
    const next = cams[(cams.findIndex((c) => c.id === first.id) + 1) % cams.length];
    shots.push({ time: 0, cameraId: first.id }, { time: sec, cameraId: next.id });
  } else {
    const active = programCameraAt(sec) ?? cams[0];
    const next = cams[(cams.findIndex((c) => c.id === active.id) + 1) % cams.length];
    shots.push({ time: sec, cameraId: next.id });
  }
  normalizeShots(state.cameras);
  director.setView('program'); // see the cut immediately
  notify();
}

/** Drag a cut boundary: snap to frames, clamp between its neighbours. */
function moveCut(index: number, seconds: number): void {
  const shots = state.cameras.shots;
  if (index <= 0 || index >= shots.length) return; // the first cut is pinned to 0
  const fps = state.fps;
  const snapped = Math.round(seconds * fps) / fps;
  const min = shots[index - 1].time + 1 / fps;
  const max =
    (index + 1 < shots.length ? shots[index + 1].time : clipDurationSeconds()) - 1 / fps;
  shots[index].time = Math.min(Math.max(snapped, min), Math.max(min, max));
  notify();
}

/** Remove a cut; the previous shot extends over its range (merge left). */
function deleteCut(index: number): void {
  const shots = state.cameras.shots;
  if (index < 0 || index >= shots.length) return;
  shots.splice(index, 1);
  normalizeShots(state.cameras);
  notify();
}

function reassignShot(index: number, cameraId: string): void {
  const shot = state.cameras.shots[index];
  if (!shot || !findCamera(state.cameras, cameraId)) return;
  shot.cameraId = cameraId;
  notify();
}

function selectCamera(id: string): void {
  state.selectedCameraId = id;
  state.selectedKeyframe = null;
  notify();
}

/** Toggle looking through a camera; selecting it as a side effect. */
function lookThroughCamera(id: string): void {
  state.selectedCameraId = id;
  director.setView(state.cameraView === id ? 'free' : id);
}

function deleteCamera(id: string): void {
  const cam = findCamera(state.cameras, id);
  if (!cam) return;
  if (state.cameraView === id) director.setView('free');
  state.cameras.cameras = state.cameras.cameras.filter((c) => c.id !== id);
  state.cameras.shots = state.cameras.shots.filter((s) => s.cameraId !== id);
  normalizeShots(state.cameras);
  if (state.selectedCameraId === id) {
    state.selectedCameraId = state.cameras.cameras[0]?.id ?? null;
    state.selectedKeyframe = null;
  }
  if (state.cameraView === 'program' && state.cameras.cameras.length === 0) {
    director.setView('free');
  }
  notify();
  showToast(`${cam.name} deleted.`);
}

function renameCamera(id: string, name: string): void {
  const cam = findCamera(state.cameras, id);
  if (!cam) return;
  cam.name = name;
  notify();
}

function setCameraColor(id: string, color: number): void {
  const cam = findCamera(state.cameras, id);
  if (!cam) return;
  cam.color = color;
  notify();
}

const cameraPanel = createCameraPanel({
  getSubjects: () =>
    actors
      .filter((a) => a.item.kind === 'skeleton')
      .map((a) => ({ id: a.item.id, label: a.item.label })),
  onApplyPreset: (presetId, subjectActorId) => addCameraFromPreset(presetId, subjectActorId),
  onSelectCamera: (id) => selectCamera(id),
  onLookThrough: (id) => lookThroughCamera(id),
  onDeleteCamera: (id) => deleteCamera(id),
  onRenameCamera: (id, name) => renameCamera(id, name),
  onCameraColor: (id, hex) => setCameraColor(id, parseInt(hex.slice(1), 16)),
  onSetView: (view) => director.setView(view),
  onPresetParam: (cameraId, key, value) => setPresetParam(cameraId, key, value),
  onShakeChange: (cameraId, patch) => setCameraShake(cameraId, patch),
  shakeAmountOf: (posAmp) => Math.round((posAmp / SHAKE_MAX_POS) * 100),
  onSetKeyFromView: () => setKeyframeFromView(),
  onDeleteKey: () => void deleteSelectedKeyframe(),
  onKeyEasing: (easing) => setKeyframeEasing(easing),
  onAimChange: (cameraId, mode, opts) => setCameraAim(cameraId, mode, opts),
  onExportCameras: () => exportCameras(),
  onImportCameras: (file) => importCamerasFile(file),
});

// Live camera rotation readout (degrees) as the user orbits.
function updateCameraReadout(): void {
  const r = viewport.camera.rotation;
  const deg = 180 / Math.PI;
  sidebar.setCameraInfo({ x: r.x * deg, y: r.y * deg, z: r.z * deg });
}
viewport.controls.addEventListener('change', updateCameraReadout);
updateCameraReadout();

function setBackground(color: number): void {
  state.background = color;
  environment.setBackground(color);
  loop.renderOnce();
  notify();
}

function setAspect(key: AspectKey): void {
  state.aspect = key;
  viewport.setCropAspect(aspectRatioValue(key));
  loop.renderOnce();
  notify();
}

function setResolution(key: ResolutionKey): void {
  state.resolution = key;
  notify();
}

const controlbar = createControlbar({
  onTogglePlay: () => timeline.togglePlay(),
  onScrub: (frame) => timeline.scrubToFrame(frame),
  onStep: (delta) => timeline.stepFrame(delta),
  onJumpStart: () => timeline.jumpToStart(),
  onJumpEnd: () => timeline.jumpToEnd(),
  onToggleUnit: () => timeline.setTimeUnit(state.timeUnit === 'seconds' ? 'timecode' : 'seconds'),
  onMarkStart: () => timeline.markStart(),
  onMarkEnd: () => timeline.markEnd(),
  onSetStart: (frame) => timeline.markStart(frame),
  onSetEnd: (frame) => timeline.markEnd(frame),
  onExportGlb: () => runGlbExport(),
  onExportMp4: () => runVideoExport(),
  onGlbFps: (fps) => {
    state.glbExportFps = fps;
    notify();
  },
  onMp4Fps: (fps) => {
    state.videoExportFps = fps;
    notify();
  },
});

const cameraStrip = createCameraStrip({
  onAddCut: () => addCutAtPlayhead(),
  onMoveCut: (index, seconds) => moveCut(index, seconds),
  onDeleteCut: (index) => deleteCut(index),
  onReassignShot: (index, cameraId) => reassignShot(index, cameraId),
  onSelectShot: (index) => {
    const shot = state.cameras.shots[index];
    if (shot) selectCamera(shot.cameraId);
  },
  onLookThroughShot: (index) => {
    const shot = state.cameras.shots[index];
    if (shot) {
      state.selectedCameraId = shot.cameraId;
      director.setView(shot.cameraId);
    }
  },
  onSelectKeyframe: (index) => selectKeyframe(index),
  onMoveKeyframe: (index, seconds) => moveKeyframeTime(index, seconds),
});

// Motive-style keyboard transport (ignored while typing in a field).
window.addEventListener('keydown', (e) => {
  if (state.totalFrames === 0 || state.exporting) return;
  const tag = (e.target as HTMLElement)?.tagName;
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;

  switch (e.key) {
    case ' ':
      e.preventDefault();
      timeline.togglePlay();
      break;
    case 'ArrowLeft':
      e.preventDefault();
      timeline.stepFrame(e.shiftKey ? -10 : -1);
      break;
    case 'ArrowRight':
      e.preventDefault();
      timeline.stepFrame(e.shiftKey ? 10 : 1);
      break;
    case 'Home':
      e.preventDefault();
      timeline.jumpToStart();
      break;
    case 'End':
      e.preventDefault();
      timeline.jumpToEnd();
      break;
    case 'c':
    case 'C':
      e.preventDefault();
      addCutAtPlayhead();
      break;
    case 'k':
    case 'K':
      e.preventDefault();
      setKeyframeFromView();
      break;
    case 'Delete':
    case 'Backspace':
      // Priority: lane key (pin/offset) → camera keyframe → camera cut.
      if (
        timelineTracks.deleteSelected() ||
        deleteSelectedKeyframe() ||
        cameraStrip.deleteSelected()
      ) {
        e.preventDefault();
      }
      break;
  }
});

subscribe(() => {
  sidebar.update();
  cameraPanel.update();
  contactsPanel.update();
  assistantPanel.update();
  controlbar.update();
  cameraStrip.update();
  timelineTracks.update();
  cameraViz.update();
  editGizmos.update();
  syncVideo();
  // 3D-over-video composite: transparent 3D background whenever the overlay
  // puts the 3D on top with "key out background" on (viewport ignores it
  // during beginExport; the state.exporting gate covers the no-aspect MP4
  // path, which renders at the live viewport size without beginExport).
  // A compositing MP4 export keeps it alive — the exporter supplies the video.
  viewport.setTransparentBackground(
    videoOverlay.hasVideo() &&
      state.video.layout === 'overlay' &&
      !state.video.onTop &&
      state.video.keyBackground &&
      (!state.exporting || videoCompositeExport)
  );
});

// --- File loading --------------------------------------------------------------
const dropzoneEl = document.getElementById('dropzone') as HTMLElement;
createDropzone(viewport.renderer.domElement.parentElement!, dropzoneEl, handleFile, showError);

function handleFile(file: DroppedFile): void {
  try {
    // Video → reference overlay.
    if (['mp4', 'mov', 'm4v', 'webm'].includes(file.ext)) {
      loadVideoFile(file.file);
      return;
    }
    // JSON → sniff: cameras file or scene file.
    if (file.ext === 'json') {
      const parsed: unknown = JSON.parse(file.text ?? '');
      const obj = parsed as { kind?: string; app?: string; source?: unknown };
      if (obj?.kind === 'cameras') {
        importCamerasProgram(parsed);
      } else if (obj?.app === 'aka-mocap-viewer' && obj?.source) {
        loadScene(file.file);
      } else {
        showError('Unrecognized JSON — expected a .scene.json or .cameras.json file.');
      }
      return;
    }
    if (!file.buffer) {
      showError(`Could not read "${file.name}".`);
      return;
    }
    // Routing:
    //  - .glb/.gltf  → retarget puppet for the current mocap source.
    //  - .fbx with a skinned mesh (e.g. Mixamo X-Bot) + existing source → puppet.
    //  - .fbx bone-only (Motive)  → (re)load as the mocap source.
    // .vrm → per-actor avatar (asks which actor when the take has several).
    if (file.ext === 'vrm') {
      if (!file.buffer) {
        showError(`Could not read "${file.name}".`);
        return;
      }
      routeVrmDrop(file.buffer, file.name);
      return;
    }

    // Models (.fbx / .glb / .gltf) parse synchronously and can stall the main
    // thread for a beat on large takes — load them behind the shared "Loading…"
    // overlay (same treatment as VRM avatars).
    void loadModelFile(file);
  } catch (err) {
    showError(`Failed to load "${file.name}": ${(err as Error).message}`);
    console.error(err);
  }
}

/** Resolve after the browser has painted a frame (double rAF), so a just-shown
 * overlay is visible before a blocking parse hogs the main thread. */
function nextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
}

/**
 * Parse a dropped model file (.fbx mocap take / character mesh, or .glb/.gltf
 * puppet) behind the loading overlay. The FBXLoader/GLTFLoader parsers run
 * synchronously, so we paint the overlay first (`nextPaint`) before the blocking
 * parse — mirroring how VRM avatars load. Skips its own overlay when one is
 * already up (e.g. a scene load driving several installs).
 */
async function loadModelFile(file: DroppedFile): Promise<void> {
  if (!file.buffer) return;
  const ownOverlay = !state.exporting;
  try {
    if (ownOverlay) {
      setExporting(true, `Loading ${file.name}…`);
      overlayProgress.removeAttribute('value');
      await nextPaint();
    }
    if (file.ext === 'glb' || file.ext === 'gltf') {
      if (!state.source) {
        showError('Load a mocap .fbx first, then drop a character mesh to retarget.');
        return;
      }
      await loadPuppetFromMesh(file);
      return;
    }

    const loaded = parseMocapFbx(file.buffer);
    if (loaded.hasMesh && state.source) {
      installPuppet(loaded.object, loaded.bones, file.name, {
        type: 'custom',
        name: file.name,
        ext: file.ext,
        buffer: file.buffer,
      });
    } else {
      installMocap(loaded, file.name);
      sourceFile = { name: file.name, ext: file.ext, buffer: file.buffer };
    }
  } catch (err) {
    showError(`Failed to load "${file.name}": ${(err as Error).message}`);
    console.error(err);
  } finally {
    if (ownOverlay) {
      setExporting(false);
      loop.renderOnce();
    }
  }
}

function installMocap(loaded: LoadedMocap, name: string): void {
  // Dispose any previous content first to avoid leaks (SPEC §3.1).
  clearSource();

  state.source = loaded.object;
  state.rootBone = loaded.rootBone;
  viewport.scene.add(loaded.object);

  // Capture the true bind pose now, BEFORE the mixers pose the skeletons — the
  // retargeter needs it to align the puppet's rest to the mocap rest.
  sourceBind = captureBindPose(loaded.object, loaded.bones);
  fingerHealer = createFingerHealer(loaded.items, sourceBind);

  // Center the camera target on the primary root bone (Hips).
  if (loaded.rootBone) {
    const p = new THREE.Vector3();
    loaded.rootBone.getWorldPosition(p);
    viewport.controls.target.copy(p);
    viewport.controls.update();
  }

  // One mixer per item, rooted at the item's group, so duplicate-named tracks
  // across actors bind to the right bones (three.js resolves names first-match).
  const mixers: THREE.AnimationMixer[] = [];
  if (loaded.clip) {
    const itemClips = buildItemClips(loaded.clip, loaded.items);
    for (const item of loaded.items) {
      const subclip = itemClips.get(item);
      if (!subclip) continue;
      const mixer = new THREE.AnimationMixer(item.root);
      mixer.clipAction(subclip).play();
      mixers.push(mixer);
    }
    timeline.setupClip(loaded.clip, mixers);
    // Follow the take's native rate so frames map 1:1 onto captured samples.
    // The render loop advances by wall-clock time, so high rates (Move.AI 180+)
    // simply drop display frames rather than slowing down.
    state.nativeFps = loaded.nativeFps;
    if (loaded.nativeFps) timeline.setFps(loaded.nativeFps);
    if (loaded.retimed && loaded.nativeFps) {
      showToast(
        `Corrected the file's key timing to its declared ${loaded.nativeFps} fps ` +
          `(clip is ${loaded.clip.duration.toFixed(1)}s).`
      );
    }
  } else {
    showError('No animation found in this FBX.');
  }

  // Build an entry + viz helper per item, with a distinct default colour per actor.
  let skeletonIdx = 0;
  actors = loaded.items.map((item) => {
    let helper: THREE.SkeletonHelper | THREE.AxesHelper;
    let octa: OctahedronBoneHelper | null = null;
    let color: number | null = null;
    if (item.kind === 'skeleton') {
      helper = new THREE.SkeletonHelper(item.root);
      (helper.material as THREE.LineBasicMaterial).linewidth = 2;
      octa = new OctahedronBoneHelper(item.bones);
      octa.visible = false; // shown by applyActorVisibility in 'octa' view
      viewport.scene.add(octa);
      color = ACTOR_PALETTE[skeletonIdx % ACTOR_PALETTE.length];
      skeletonIdx++;
    } else {
      helper = new THREE.AxesHelper(0.2); // small marker for rigid bodies
    }
    viewport.scene.add(helper);
    const entry: ActorEntry = {
      item,
      helper,
      octa,
      puppet: null,
      retargeter: null,
      vrm: null,
      avatarName: null,
      targetMap: null,
      boneOverrides: null,
      adjust: { offset: [0, 0, 0], scale: 1 },
      adjustKeys: [],
      fingerTune: defaultFingerTune(),
      fingerTuneKeys: [],
      fingerTuner: null,
      puppetBaseScale: 1,
      visible: true,
      color,
    };
    applyActorColor(entry);
    return entry;
  });

  if (loaded.clip) timeline.scrubToFrame(0);

  dropzoneEl.classList.add('has-content');
  const skeletonCount = loaded.items.filter((i) => i.kind === 'skeleton').length;
  const meta: FileMeta = {
    name,
    bones: loaded.bones.length,
    actors: skeletonCount,
    frames: state.totalFrames,
    duration: loaded.clip?.duration ?? 0,
    hasMesh: loaded.hasMesh,
    nativeFps: loaded.nativeFps,
  };
  sidebar.setFile(meta);
  refreshActorPanel();
  setViewMode('skeleton');
  loop.renderOnce();
  notify();
}

// --- Finger tuning -----------------------------------------------------------------

/**
 * Canned correction for VRM 0.x-style thumbs (rested naturally instead of the
 * 45°-out mocap convention). Values calibrated against the sample VRMs; the
 * sliders stay live for per-avatar fine-tuning.
 */
const VRM0_THUMB_PRESET = { thumbOut: -40, thumbRoll: 0, thumbCurl: 0 };

function applyVrm0ThumbPreset(tune: FingerTune, on: boolean): void {
  tune.vrm0Fix = on;
  tune.thumbOut = on ? VRM0_THUMB_PRESET.thumbOut : 0;
  tune.thumbRoll = on ? VRM0_THUMB_PRESET.thumbRoll : 0;
  tune.thumbCurl = on ? VRM0_THUMB_PRESET.thumbCurl : 0;
}

/**
 * Patch an actor's finger tune from the panel sliders. With finger keys
 * present this AUTO-KEYS at the playhead (same contract as the offsets —
 * otherwise the per-tick evaluation would overwrite the edit).
 */
function setFingerTune(id: string, patch: Partial<FingerTune>): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a) return;
  if (patch.vrm0Fix !== undefined) {
    applyVrm0ThumbPreset(a.fingerTune, patch.vrm0Fix);
  }
  for (const key of FINGER_TUNE_FIELDS) {
    if (patch[key] !== undefined) a.fingerTune[key] = patch[key];
  }
  if (a.fingerTuneKeys.length > 0) upsertFingerTuneKey(a, false);
  tickActors();
  loop.renderOnce();
  refreshActorPanel();
  notify();
}

/** Record the actor's current finger tune as a key (playhead unless overridden). */
function upsertFingerTuneKey(a: ActorEntry, announce: boolean, atTime?: number): void {
  const t = atTime ?? frameToSeconds(state.currentFrame);
  const key: FingerTuneKey = { time: t, tune: { ...a.fingerTune } };
  const existing = a.fingerTuneKeys.findIndex((k) => Math.abs(k.time - t) < 0.5 / state.fps);
  if (existing >= 0) a.fingerTuneKeys[existing] = key;
  else a.fingerTuneKeys.push(key);
  a.fingerTuneKeys.sort((x, y) => x.time - y.time);
  refreshActorPanel();
  if (announce) {
    notify();
    showToast(
      existing >= 0
        ? 'Finger key updated.'
        : `Finger key added to ${a.avatarName ?? a.item.label} — slider edits auto-key at the playhead now.`
    );
  }
}

function setFingerKeyAtPlayhead(id: string): void {
  const a = actors.find((x) => x.item.id === id);
  if (a) upsertFingerTuneKey(a, true);
}

function deleteFingerTuneKey(id: string, index: number): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a || !a.fingerTuneKeys[index]) return;
  a.fingerTuneKeys.splice(index, 1);
  refreshActorPanel();
  notify();
}

/** Retime a finger key from its lane diamond (frame-snapped, clamped). */
function moveFingerTuneKeyTime(id: string, index: number, seconds: number): void {
  const a = actors.find((x) => x.item.id === id);
  const key = a?.fingerTuneKeys[index];
  if (!a || !key) return;
  const fps = state.fps;
  const min = index > 0 ? a.fingerTuneKeys[index - 1].time + 1 / fps : 0;
  const max =
    index + 1 < a.fingerTuneKeys.length
      ? a.fingerTuneKeys[index + 1].time - 1 / fps
      : clipDurationSeconds();
  key.time = Math.min(Math.max(Math.round(seconds * fps) / fps, min), Math.max(min, max));
  notify();
}

// --- VRM avatars (per-actor) -----------------------------------------------------

/** Parse a .vrm and retarget it onto one specific actor's skeleton. */
async function assignVrmToActor(actorId: string, buffer: ArrayBuffer, name: string): Promise<void> {
  const a = actors.find((x) => x.item.id === actorId);
  if (!a || a.item.kind !== 'skeleton') return;
  if (!sourceBind) {
    showError('Load a mocap .fbx before assigning avatars.');
    return;
  }
  // Don't fight an outer overlay (scene load wraps multiple avatar loads).
  const ownOverlay = !state.exporting;
  try {
    if (ownOverlay) {
      setExporting(true, `Loading ${name}…`);
      overlayProgress.removeAttribute('value');
    }
    const { vrm, object, targetMap } = await parseVrm(buffer);
    const retargeter = createRetargeter(
      a.item.bones,
      targetMap,
      sourceBind,
      a.boneOverrides ?? undefined,
      a.adjust
    );
    if (retargeter.pairs.length === 0) {
      disposeVrm(vrm);
      showError(`No matching bones between the mocap and "${name}".`);
      return;
    }
    clearActorPuppet(a);
    enableShadowCasting(object);
    viewport.scene.add(object);
    a.puppet = object;
    a.retargeter = retargeter;
    if (state.matchProportions) retargeter.setProportionMatch(true);
    a.vrm = vrm;
    a.avatarName = name;
    a.targetMap = targetMap;
    // Finger tuner axes derive from the rest pose — build before the first retarget.
    a.fingerTuner = createFingerTuner((c) => targetMap.get(c) ?? null);
    // VRM 0.x rigs rest thumbs unlike the mocap convention (docs/VRM_THUMBS.md):
    // auto-apply the thumb fix unless the user already tuned this actor.
    const metaVersion =
      (vrm.meta as { metaVersion?: string } | undefined)?.metaVersion ?? '0';
    if (
      metaVersion === '0' &&
      !isFingerTuneActive(a.fingerTune) &&
      a.fingerTuneKeys.length === 0
    ) {
      applyVrm0ThumbPreset(a.fingerTune, true);
      showToast(`${name}: VRM 0.x thumb fix applied — fine-tune under Adjust actor.`);
    } else if (metaVersion !== '0' && a.fingerTune.vrm0Fix && a.fingerTuneKeys.length === 0) {
      applyVrm0ThumbPreset(a.fingerTune, false);
    }
    applyActorVisibility();
    sidebar.setPuppetAvailable(true);
    refreshActorPanel();
    setViewMode('mesh');
    showToast(`${name} → ${a.item.label}.`);
  } catch (err) {
    showError(`Couldn't load "${name}": ${(err as Error).message}`);
    console.error(err);
  } finally {
    if (ownOverlay) setExporting(false);
  }
}

/** Dropped .vrm: assign directly with one actor, else ask which actor wears it. */
function routeVrmDrop(buffer: ArrayBuffer, name: string): void {
  const skeletons = actors.filter((a) => a.item.kind === 'skeleton');
  if (skeletons.length === 0) {
    showError('Load a mocap .fbx first, then drop a VRM avatar.');
    return;
  }
  if (skeletons.length === 1) {
    void assignVrmToActor(skeletons[0].item.id, buffer, name);
    return;
  }
  // Multiple actors: ask which one wears this avatar.
  avatarAssignMsg.textContent = `Which actor should wear "${name}"?`;
  avatarAssignList.innerHTML = '';
  for (const a of skeletons) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn small';
    btn.textContent = a.avatarName ? `${a.item.label} (${a.avatarName})` : a.item.label;
    btn.addEventListener('click', () => {
      avatarAssign.hidden = true;
      void assignVrmToActor(a.item.id, buffer, name);
    });
    avatarAssignList.appendChild(btn);
  }
  avatarAssign.hidden = false;
}

const avatarAssign = document.getElementById('avatar-assign') as HTMLElement;
const avatarAssignMsg = document.getElementById('avatar-assign-msg') as HTMLElement;
const avatarAssignList = document.getElementById('avatar-assign-list') as HTMLElement;
document.getElementById('avatar-assign-cancel')!.addEventListener('click', () => {
  avatarAssign.hidden = true;
});

// Per-actor "VRM…" button → hidden file picker; remembers which actor asked.
const avatarFileInput = document.getElementById('avatar-file-input') as HTMLInputElement;
let pendingAvatarActor: string | null = null;
avatarFileInput.addEventListener('change', () => {
  const file = avatarFileInput.files?.[0];
  avatarFileInput.value = '';
  const actorId = pendingAvatarActor;
  pendingAvatarActor = null;
  if (!file || !actorId) return;
  void file.arrayBuffer().then((buffer) => assignVrmToActor(actorId, buffer, file.name));
});

// --- Contact pins --------------------------------------------------------------------

/** New pin: right hand of the first (preferably avatared) actor, held in place. */
function addContactPin(): void {
  const a = actors.find((x) => x.retargeter && x.puppet) ?? actors.find((x) => x.item.kind === 'skeleton');
  if (!a) {
    showError('Load a mocap take before pinning a hand or foot.');
    return;
  }
  const limb: ContactLimb = 'righthand';
  const p = new THREE.Vector3();
  const end = chainBoneOf(a, LIMB_CHAINS[limb][2], state.viewMode === 'mesh');
  if (end) {
    end.updateWorldMatrix(true, false);
    p.setFromMatrixPosition(end.matrixWorld);
  }
  const pin: ContactPin = {
    id: crypto.randomUUID(),
    actorId: a.item.id,
    limb,
    target: {
      kind: 'point',
      point: [+p.x.toFixed(3), +p.y.toFixed(3), +p.z.toFixed(3)],
    },
    keys: [{ time: frameToSeconds(state.currentFrame), value: 1 }],
    enabled: true,
  };
  state.contacts.push(pin);
  state.selectedContactId = pin.id;
  notify();
  showToast('Pin added — it holds where the limb is right now. Keyframe the influence to blend.');
}

function updateContactPin(
  id: string,
  patch: {
    actorId?: string;
    limb?: ContactLimb;
    targetKind?: 'point' | 'bone';
    point?: [number, number, number];
    targetActorId?: string;
    targetBone?: string;
    enabled?: boolean;
    targetOffset?: [number, number, number];
    matchRotation?: boolean;
    targetRotate?: [number, number, number];
  }
): void {
  const pin = state.contacts.find((p) => p.id === id);
  if (!pin) return;
  if (patch.actorId) pin.actorId = patch.actorId;
  if (patch.limb) pin.limb = patch.limb;
  if (patch.enabled !== undefined) pin.enabled = patch.enabled;
  if (patch.targetKind === 'point' && pin.target.kind !== 'point') {
    // Capture the limb's current position as the new hold point.
    const a = actors.find((x) => x.item.id === pin.actorId);
    const p = new THREE.Vector3();
    const end = a ? chainBoneOf(a, LIMB_CHAINS[pin.limb][2], state.viewMode === 'mesh') : null;
    if (end) {
      end.updateWorldMatrix(true, false);
      p.setFromMatrixPosition(end.matrixWorld);
    }
    pin.target = { kind: 'point', point: [+p.x.toFixed(3), +p.y.toFixed(3), +p.z.toFixed(3)] };
  } else if (patch.targetKind === 'bone' && pin.target.kind !== 'bone') {
    const other = actors.find((x) => x.item.kind === 'skeleton' && x.item.id !== pin.actorId);
    pin.target = { kind: 'bone', actorId: other?.item.id ?? pin.actorId, bone: 'hips' };
  }
  if (patch.point && pin.target.kind === 'point') pin.target.point = patch.point;
  if (pin.target.kind === 'bone') {
    if (patch.targetActorId) pin.target.actorId = patch.targetActorId;
    if (patch.targetBone) pin.target.bone = patch.targetBone;
    if (patch.targetOffset) pin.target.offset = patch.targetOffset;
    if (patch.matchRotation !== undefined) pin.target.matchRotation = patch.matchRotation;
    if (patch.targetRotate) pin.target.rotate = patch.targetRotate;
  }
  notify();
}

/** Add/update an influence key (playhead unless overridden; frame-snapped). */
function setContactKey(id: string, value: number, atTime?: number): void {
  const pin = state.contacts.find((p) => p.id === id);
  if (!pin) return;
  const t = atTime ?? frameToSeconds(state.currentFrame);
  const existing = pin.keys.findIndex((k) => Math.abs(k.time - t) < 0.5 / state.fps);
  if (existing >= 0) pin.keys[existing] = { time: t, value };
  else pin.keys.push({ time: t, value });
  pin.keys.sort((a, b) => a.time - b.time);
  notify();
}

function deleteContactKey(id: string, index: number): void {
  const pin = state.contacts.find((p) => p.id === id);
  if (!pin || !pin.keys[index]) return;
  pin.keys.splice(index, 1);
  notify();
}

function deleteContactPin(id: string): void {
  state.contacts = state.contacts.filter((p) => p.id !== id);
  if (state.selectedContactId === id) state.selectedContactId = null;
  notify();
}

const contactsPanel = createContactsPanel({
  getActors: () =>
    actors
      .filter((a) => a.item.kind === 'skeleton')
      .map((a) => ({ id: a.item.id, label: a.avatarName ?? a.item.label })),
  onAddPin: () => addContactPin(),
  onSelectPin: (id) => {
    state.selectedContactId = id;
    state.selectedKeyframe = null; // one live gizmo: the pin wins now
    notify();
  },
  onDeletePin: (id) => deleteContactPin(id),
  onPinChange: (id, patch) => updateContactPin(id, patch),
  onSetKey: (id, value) => setContactKey(id, value),
  onDeleteKey: (id, index) => deleteContactKey(id, index),
});

// --- Timeline lanes (contact pins; offset layers join in the Adjust work) --------------

/** Select a pin's influence key from its lane and scrub to it. */
function selectContactKeyOnLane(pinId: string, index: number): void {
  state.selectedContactId = pinId;
  state.selectedKeyframe = null;
  const key = state.contacts.find((p) => p.id === pinId)?.keys[index];
  if (key) timeline.scrubToFrame(Math.round(key.time * state.fps));
  else notify();
}

/** Retime an influence key from a lane diamond drag (frame-snapped, clamped). */
function moveContactKeyTime(pinId: string, index: number, seconds: number): void {
  const pin = state.contacts.find((p) => p.id === pinId);
  const key = pin?.keys[index];
  if (!pin || !key) return;
  const fps = state.fps;
  const min = index > 0 ? pin.keys[index - 1].time + 1 / fps : 0;
  const max =
    index + 1 < pin.keys.length ? pin.keys[index + 1].time - 1 / fps : clipDurationSeconds();
  key.time = Math.min(Math.max(Math.round(seconds * fps) / fps, min), Math.max(min, max));
  notify();
}

/** One timeline lane per contact pin: influence envelope + key diamonds. */
function pinLanes(): Lane[] {
  const dur = clipDurationSeconds();
  return state.contacts.map((pin) => {
    const a = actors.find((x) => x.item.id === pin.actorId);
    // The envelope extends to the clip edges (evalInfluence clamps there too).
    const pts =
      pin.keys.length > 0
        ? [
            { time: 0, value: pin.keys[0].value },
            ...pin.keys,
            { time: dur, value: pin.keys[pin.keys.length - 1].value },
          ]
        : [];
    const blocks: Lane['blocks'] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const avg = (pts[i].value + pts[i + 1].value) / 2;
      if (avg > 0.02 && pts[i + 1].time > pts[i].time) {
        blocks.push({ start: pts[i].time, end: pts[i + 1].time, opacity: avg });
      }
    }
    return {
      id: `pin:${pin.id}`,
      label: `${LIMB_LABELS[pin.limb]} · ${a?.avatarName ?? a?.item.label ?? '?'}`,
      color: a?.color ?? 0x4f9cff,
      enabled: pin.enabled,
      blocks,
      keys: pin.keys.map((k) => k.time),
      onToggle: () => updateContactPin(pin.id, { enabled: !pin.enabled }),
      onSelectKey: (index) => selectContactKeyOnLane(pin.id, index),
      onMoveKey: (index, seconds) => moveContactKeyTime(pin.id, index, seconds),
      onDeleteKey: (index) => deleteContactKey(pin.id, index),
    };
  });
}

/**
 * A soft additive layer per adjusted avatar: the lane's envelope shows where
 * and how strongly the offset applies (opacity ∝ magnitude, normalized to the
 * lane's own peak), with key diamonds when the offset is keyframed.
 */
function offsetLanes(): Lane[] {
  const dur = clipDurationSeconds();
  const magnitude = (offset: number[], scale: number): number =>
    Math.hypot(offset[0], offset[1], offset[2]) + Math.abs(scale - 1);
  const lanes: Lane[] = [];
  for (const a of actors) {
    if (a.item.kind !== 'skeleton') continue;
    const hasKeys = a.adjustKeys.length > 0;
    if (!hasKeys && magnitude(a.adjust.offset, a.adjust.scale) < 0.01) continue;

    let blocks: Lane['blocks'];
    let keys: number[] = [];
    if (hasKeys) {
      const pts = [
        { time: 0, m: magnitude(a.adjustKeys[0].offset, a.adjustKeys[0].scale) },
        ...a.adjustKeys.map((k) => ({ time: k.time, m: magnitude(k.offset, k.scale) })),
        {
          time: dur,
          m: magnitude(
            a.adjustKeys[a.adjustKeys.length - 1].offset,
            a.adjustKeys[a.adjustKeys.length - 1].scale
          ),
        },
      ];
      const peak = Math.max(...pts.map((p) => p.m), 0.001);
      blocks = [];
      for (let i = 0; i < pts.length - 1; i++) {
        const avg = (pts[i].m + pts[i + 1].m) / 2 / peak;
        if (avg > 0.02 && pts[i + 1].time > pts[i].time) {
          blocks.push({ start: pts[i].time, end: pts[i + 1].time, opacity: avg });
        }
      }
      keys = a.adjustKeys.map((k) => k.time);
    } else {
      blocks = [{ start: 0, end: dur, opacity: 0.35 }]; // static offset, whole clip
    }

    const id = a.item.id;
    lanes.push({
      id: `offset:${id}`,
      label: `Offset · ${a.avatarName ?? a.item.label}`,
      color: a.color ?? 0xffb347,
      blocks,
      keys,
      onSelectKey: (index) => {
        const key = actors.find((x) => x.item.id === id)?.adjustKeys[index];
        if (key) timeline.scrubToFrame(Math.round(key.time * state.fps));
      },
      onMoveKey: (index, seconds) => moveAdjustKeyTime(id, index, seconds),
      onDeleteKey: (index) => deleteAdjustKey(id, index),
    });
  }
  return lanes;
}

/** A "Hands" lane per actor with finger-tune keys (envelope ∝ tune magnitude). */
function fingerLanes(): Lane[] {
  const dur = clipDurationSeconds();
  const magnitude = (t: FingerTune): number =>
    FINGER_TUNE_FIELDS.reduce((sum, f) => sum + Math.abs(t[f]), 0);
  const lanes: Lane[] = [];
  for (const a of actors) {
    if (a.item.kind !== 'skeleton' || a.fingerTuneKeys.length === 0) continue;
    const pts = [
      { time: 0, m: magnitude(a.fingerTuneKeys[0].tune) },
      ...a.fingerTuneKeys.map((k) => ({ time: k.time, m: magnitude(k.tune) })),
      { time: dur, m: magnitude(a.fingerTuneKeys[a.fingerTuneKeys.length - 1].tune) },
    ];
    const peak = Math.max(...pts.map((p) => p.m), 0.001);
    const blocks: Lane['blocks'] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const avg = (pts[i].m + pts[i + 1].m) / 2 / peak;
      if (avg > 0.02 && pts[i + 1].time > pts[i].time) {
        blocks.push({ start: pts[i].time, end: pts[i + 1].time, opacity: avg });
      }
    }
    const id = a.item.id;
    lanes.push({
      id: `hands:${id}`,
      label: `Hands · ${a.avatarName ?? a.item.label}`,
      color: a.color ?? 0x53d0d8,
      blocks,
      keys: a.fingerTuneKeys.map((k) => k.time),
      onSelectKey: (index) => {
        const key = actors.find((x) => x.item.id === id)?.fingerTuneKeys[index];
        if (key) timeline.scrubToFrame(Math.round(key.time * state.fps));
      },
      onMoveKey: (index, seconds) => moveFingerTuneKeyTime(id, index, seconds),
      onDeleteKey: (index) => deleteFingerTuneKey(id, index),
    });
  }
  return lanes;
}

const timelineTracks = createTimelineTracks({
  getLanes: () => [...pinLanes(), ...offsetLanes(), ...fingerLanes()],
});

// --- Edit gizmos (pin point + actor offset + deviation lines) --------------------------

/** Offset anchor for one actor: where mocap puts it vs where the adjust moved it. */
function offsetAnchorOf(a: ActorEntry): OffsetAnchor | null {
  if (!a.retargeter || !a.puppet || state.viewMode !== 'mesh') return null;
  const { raw, adjusted } = a.retargeter.hipsInfo;
  return {
    actorId: a.item.id,
    color: a.color ?? 0xffffff,
    // `from` = mocap placement with the size term applied but no offset, so the
    // dotted line is exactly the offset vector.
    from: new THREE.Vector3(raw.x, raw.y * a.adjust.scale, raw.z),
    to: adjusted.clone(),
    offsetLength: Math.hypot(a.adjust.offset[0], a.adjust.offset[1], a.adjust.offset[2]),
  };
}

/** The offset marker was dragged: derive the new offset from the raw placement. */
function moveOffsetMarker(actorId: string, position: THREE.Vector3): void {
  const a = actors.find((x) => x.item.id === actorId);
  if (!a?.retargeter) return;
  const raw = a.retargeter.hipsInfo.raw;
  setActorAdjust(actorId, {
    x: +(position.x - raw.x).toFixed(3),
    y: +(position.y - raw.y * a.adjust.scale).toFixed(3),
    z: +(position.z - raw.z).toFixed(3),
  });
  refreshActorPanel(); // keep the Adjust panel's XYZ inputs live
}

const editGizmos = createEditGizmos(viewport, {
  getSelectedPinPoint: () => {
    const pin = state.contacts.find((p) => p.id === state.selectedContactId);
    if (!pin || pin.target.kind !== 'point') return null;
    return { pinId: pin.id, point: pin.target.point };
  },
  // The offset gizmo is the "idle" gizmo: any more specific selection (a pin,
  // a camera keyframe, a point-aim camera) takes the stage instead.
  getOffsetTarget: () => {
    if (state.selectedKeyframe !== null) return null;
    const cam = findCamera(state.cameras, state.selectedCameraId);
    if (cam?.aim.mode === 'point') return null;
    const actorId = sidebar.getAdjustActor();
    const a = actorId ? actors.find((x) => x.item.id === actorId) : undefined;
    return a ? offsetAnchorOf(a) : null;
  },
  getDeviations: () =>
    // "None" in the Adjust dropdown = gizmo-free preview, deviations included.
    sidebar.getAdjustActor()
      ? actors
          .map((a) => offsetAnchorOf(a))
          .filter((d): d is OffsetAnchor => d !== null && d.offsetLength > 0.01)
      : [],
  onMovePinPoint: (pinId, position) => {
    updateContactPin(pinId, {
      point: [+position.x.toFixed(3), +position.y.toFixed(3), +position.z.toFixed(3)],
    });
  },
  onMoveOffsetMarker: (actorId, position) => moveOffsetMarker(actorId, position),
});

// --- Actor adjustments (offset + size) -----------------------------------------------

/** Reflect an actor's adjust values onto its puppet (root scale) and re-render. */
function applyActorAdjust(a: ActorEntry): void {
  if (a.puppet) a.puppet.scale.setScalar(a.puppetBaseScale * a.adjust.scale);
  tickActors();
  loop.renderOnce();
}

/**
 * Update one actor's offset/size. Mutates in place — the retargeter holds a
 * ref. With adjust keys present this AUTO-KEYS at the playhead (otherwise the
 * per-tick key evaluation would immediately overwrite the edit).
 */
function setActorAdjust(
  id: string,
  patch: { x?: number; y?: number; z?: number; scale?: number }
): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a) return;
  if (patch.x !== undefined) a.adjust.offset[0] = patch.x;
  if (patch.y !== undefined) a.adjust.offset[1] = patch.y;
  if (patch.z !== undefined) a.adjust.offset[2] = patch.z;
  if (patch.scale !== undefined) a.adjust.scale = patch.scale;
  if (a.adjustKeys.length > 0) upsertAdjustKey(a, false);
  applyActorAdjust(a);
}

function resetActorAdjust(id: string): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a) return;
  a.adjust.offset[0] = 0;
  a.adjust.offset[1] = 0;
  a.adjust.offset[2] = 0;
  a.adjust.scale = 1;
  a.adjustKeys = [];
  applyActorAdjust(a);
  refreshActorPanel();
  notify();
}

/** Record the actor's current adjust values as a key (playhead unless overridden). */
function upsertAdjustKey(a: ActorEntry, announce: boolean, atTime?: number): void {
  const t = atTime ?? frameToSeconds(state.currentFrame);
  const key: AdjustKey = {
    time: t,
    offset: [...a.adjust.offset] as [number, number, number],
    scale: a.adjust.scale,
  };
  const existing = a.adjustKeys.findIndex((k) => Math.abs(k.time - t) < 0.5 / state.fps);
  if (existing >= 0) a.adjustKeys[existing] = key;
  else a.adjustKeys.push(key);
  a.adjustKeys.sort((x, y) => x.time - y.time);
  refreshActorPanel();
  if (announce) {
    notify();
    showToast(
      existing >= 0
        ? 'Offset key updated.'
        : `Offset key added to ${a.avatarName ?? a.item.label} — further edits auto-key at the playhead.`
    );
  }
}

function setAdjustKeyAtPlayhead(id: string): void {
  const a = actors.find((x) => x.item.id === id);
  if (a) upsertAdjustKey(a, true);
}

function deleteAdjustKey(id: string, index: number): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a || !a.adjustKeys[index]) return;
  a.adjustKeys.splice(index, 1);
  refreshActorPanel();
  notify();
}

/** Retime an offset key from its lane diamond (frame-snapped, clamped). */
function moveAdjustKeyTime(id: string, index: number, seconds: number): void {
  const a = actors.find((x) => x.item.id === id);
  const key = a?.adjustKeys[index];
  if (!a || !key) return;
  const fps = state.fps;
  const min = index > 0 ? a.adjustKeys[index - 1].time + 1 / fps : 0;
  const max =
    index + 1 < a.adjustKeys.length
      ? a.adjustKeys[index + 1].time - 1 / fps
      : clipDurationSeconds();
  key.time = Math.min(Math.max(Math.round(seconds * fps) / fps, min), Math.max(min, max));
  notify();
}

// --- Bone mapping ------------------------------------------------------------------

/**
 * Experimental "match mocap proportions": rescale every avatar's chain segments
 * to the mocap's bind lengths so joints land exactly on the actor's (meshes
 * warp — for hand-contact shots hidden by framing). One global toggle.
 */
function setMatchProportions(on: boolean): void {
  state.matchProportions = on;
  for (const a of actors) a.retargeter?.setProportionMatch(on);
  tickActors();
  loop.renderOnce();
  notify();
}

/** Recreate an actor's retargeter (after a bone-mapping change). */
function rebuildActorRetargeter(a: ActorEntry): void {
  if (!a.puppet || !sourceBind) return;
  // Restore original proportions first — the new retargeter must measure the
  // unscaled rig, and the old one owns the stored originals.
  a.retargeter?.setProportionMatch(false);
  const targets = a.targetMap ?? collectBones(a.puppet);
  const retargeter = createRetargeter(
    a.item.bones,
    targets,
    sourceBind,
    a.boneOverrides ?? undefined,
    a.adjust
  );
  a.retargeter = retargeter;
  if (state.matchProportions) retargeter.setProportionMatch(true);
  tickActors();
  loop.renderOnce();
  const remaps = a.boneOverrides?.size ?? 0;
  showToast(
    `${a.item.label}: ${retargeter.pairs.length} bones mapped` +
      (remaps > 0 ? ` (${remaps} manual).` : '.')
  );
}

const boneMapModal = createBoneMapModal({
  onApply: (actorId, overrides) => {
    const a = actors.find((x) => x.item.id === actorId);
    if (!a) return;
    a.boneOverrides = overrides.size > 0 ? overrides : null;
    rebuildActorRetargeter(a);
  },
});

/** Open the mapping editor pre-filled with this actor's auto-matches + remaps. */
function openBoneMapFor(actorId: string): void {
  const a = actors.find((x) => x.item.id === actorId);
  if (!a) return;
  const boneNames = a.item.bones.map((b) => b.name);

  // Auto-match: first source bone per canonical slot (same rule the retargeter uses).
  const auto: Record<string, string> = {};
  for (const bone of a.item.bones) {
    const key = toCanonical(bone.name);
    if (key && !(key in auto)) auto[key] = bone.name;
  }
  const current: Record<string, string> = { ...auto };
  if (a.boneOverrides) {
    for (const [key, name] of a.boneOverrides) current[key] = name;
  }

  boneMapModal.open({ actorId, actorLabel: a.item.label, boneNames, current, auto });
}

/** Load a .glb/.gltf character mesh and install it as a retarget puppet. */
async function loadPuppetFromMesh(file: DroppedFile): Promise<void> {
  if (!file.buffer) return;
  const buffer = file.buffer;
  const { object, bones } = await parseTargetMesh(buffer, file.ext);
  installPuppet(object, bones, file.name, {
    type: 'custom',
    name: file.name,
    ext: file.ext,
    buffer,
  });
}

/** Load a reference video as an overlay, applying the current overlay settings. */
function loadVideoFile(file: File): void {
  videoOverlay.setSource(file);
  state.video.name = file.name;
  videoOverlay.setLayout(state.video.layout);
  videoOverlay.setOnTop(state.video.onTop);
  videoOverlay.setVideoOpacity(state.video.opacity);
  videoOverlay.setSceneOpacity(state.video.sceneOpacity);
  applyVideoViewportRegion();
  sidebar.setVideo(file.name);
  showToast(`Reference video "${file.name}" loaded.`);
  notify();
}

function removeVideo(): void {
  videoOverlay.clear();
  state.video.name = null;
  viewport.setRenderRegion(null);
  sidebar.setVideo(null);
  loop.renderOnce();
  notify();
}

/** Load one of the baked-in previz avatars (X-Bot female / Y-Bot male). */
async function loadBakedPuppet(which: 'xbot' | 'ybot'): Promise<void> {
  if (!state.source) {
    showError('Load a mocap .fbx first, then pick a preview mesh.');
    return;
  }
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}avatars/${which}.fbx`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { object, bones } = await parseTargetMesh(await res.arrayBuffer(), 'fbx');
    installPuppet(object, bones, which === 'xbot' ? 'X-Bot' : 'Y-Bot', { type: 'baked', id: which });
  } catch (err) {
    showError(`Couldn't load ${which}: ${(err as Error).message}`);
  }
}

/**
 * Retarget a character mesh onto every actor skeleton: the mesh is cloned once
 * per actor (SkeletonUtils.clone) so each has an independent, rebindable skeleton.
 */
function installPuppet(
  object: THREE.Object3D,
  bones: THREE.Object3D[],
  name: string,
  source: PuppetSource
): void {
  clearPuppets();

  if (!sourceBind) {
    showError('Load a mocap .fbx before retargeting.');
    return;
  }

  const skeletonActors = actors.filter((a) => a.item.kind === 'skeleton');
  let assigned = 0;
  skeletonActors.forEach((a, i) => {
    const puppetObject = i === 0 ? object : cloneSkinned(object);
    const puppetBones = i === 0 ? bones : collectBones(puppetObject);
    const retargeter = createRetargeter(
      a.item.bones,
      puppetBones,
      sourceBind!,
      a.boneOverrides ?? undefined,
      a.adjust
    );
    if (retargeter.pairs.length === 0) {
      if (i === 0) disposeObject(puppetObject);
      return;
    }
    makeMaterialsUnique(puppetObject); // so per-actor tinting stays independent
    enableShadowCasting(puppetObject);
    viewport.scene.add(puppetObject);
    a.puppet = puppetObject;
    a.retargeter = retargeter;
    if (state.matchProportions) retargeter.setProportionMatch(true);
    a.fingerTuner = createFingerTuner(
      (c) => puppetBones.find((b) => toCanonical(b.name) === c) ?? null
    );
    applyActorColor(a);
    assigned++;
  });

  if (assigned === 0) {
    showError('No matching bones between mocap and mesh — cannot retarget.');
    return;
  }

  currentPuppetSource = source;
  sidebar.setPuppetAvailable(true);
  refreshActorPanel();
  setViewMode('mesh');
  const who = assigned > 1 ? `${assigned} actors` : name;
  showToast(`Retargeting onto ${who}.`);
}

/** Let every mesh in a puppet cast the ground shadow (free while shadows are off). */
function enableShadowCasting(object: THREE.Object3D): void {
  object.traverse((c) => {
    if ((c as THREE.Mesh).isMesh) c.castShadow = true;
  });
}

function collectBones(root: THREE.Object3D): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  root.traverse((c) => {
    if ((c as THREE.Bone).isBone) out.push(c);
  });
  return out;
}

function setViewMode(mode: ViewMode): void {
  state.viewMode = mode;
  applyActorVisibility();
  tickActors();
  loop.renderOnce();
  notify();
}

/** Reflect per-actor visibility + the current view mode onto the scene objects. */
function applyActorVisibility(): void {
  const mesh = state.viewMode === 'mesh';
  const octa = state.viewMode === 'octa';
  for (const a of actors) {
    if (a.puppet) a.puppet.visible = mesh && a.visible;
    if (a.octa) a.octa.visible = octa && a.visible;
    if (a.helper) {
      if (a.item.kind === 'skeleton') {
        // Show the stick figure unless octahedrons or a puppet cover it.
        a.helper.visible = a.visible && !octa && (!mesh || !a.puppet);
      } else {
        a.helper.visible = a.visible; // prop marker shows in every view
      }
    }
  }
}

function toggleActorVisible(id: string, visible: boolean): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a) return;
  a.visible = visible;
  applyActorVisibility();
  loop.renderOnce();
}

function refreshActorPanel(): void {
  sidebar.setActors(
    actors.map((a) => ({
      id: a.item.id,
      label: a.item.label,
      kind: a.item.kind,
      visible: a.visible,
      color: a.color !== null ? `#${a.color.toString(16).padStart(6, '0')}` : null,
      avatar: a.avatarName,
      mappable: a.retargeter !== null,
      adjust: {
        x: a.adjust.offset[0],
        y: a.adjust.offset[1],
        z: a.adjust.offset[2],
        scale: a.adjust.scale,
      },
      adjustKeys: a.adjustKeys.map((k) => ({
        time: k.time,
        x: k.offset[0],
        y: k.offset[1],
        z: k.offset[2],
        scale: k.scale,
      })),
      fingerTune: { ...a.fingerTune },
      fingerKeyCount: a.fingerTuneKeys.length,
    }))
  );
}

/** Tint an actor's skeleton helper and puppet materials to its assigned colour. */
function applyActorColor(a: ActorEntry): void {
  if (a.color === null) return;

  // Skeleton helper: override the default gradient with a flat actor colour.
  if (a.helper instanceof THREE.SkeletonHelper) {
    const mat = a.helper.material as THREE.LineBasicMaterial;
    mat.vertexColors = false;
    mat.color.setHex(a.color);
    mat.needsUpdate = true;
  }

  // Octahedral bones share one material per actor.
  a.octa?.material.color.setHex(a.color);

  // VRM avatars keep their authored look — tinting would ruin the textures.
  if (a.vrm) return;

  // Puppet meshes: tint each material (already made unique per puppet on install).
  a.puppet?.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const mat = m as THREE.MeshStandardMaterial;
      if (mat.color) mat.color.setHex(a.color!);
    }
  });
}

/** Clone a mesh subtree's materials so tinting one puppet doesn't affect clones. */
function makeMaterialsUnique(object: THREE.Object3D): void {
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    mesh.material = Array.isArray(mesh.material)
      ? mesh.material.map((m) => m.clone())
      : mesh.material.clone();
  });
}

function setActorColor(id: string, color: number): void {
  const a = actors.find((x) => x.item.id === id);
  if (!a) return;
  a.color = color;
  applyActorColor(a);
  loop.renderOnce();
}

function clearSource(): void {
  for (const m of state.mixers) m.stopAllAction();
  clearPuppets();
  for (const a of actors) {
    if (a.helper) {
      viewport.scene.remove(a.helper);
      disposeObject(a.helper);
    }
    if (a.octa) {
      viewport.scene.remove(a.octa);
      a.octa.dispose();
    }
  }
  actors = [];
  if (state.source) {
    viewport.scene.remove(state.source);
    disposeObject(state.source);
    state.source = null;
  }
  state.rootBone = null;
  state.nativeFps = null;
  sourceBind = null;
  fingerHealer = null;
  sourceFile = null;
  timeline.reset();
  refreshActorPanel();
}

/** Remove one actor's puppet/avatar (VRMs get their dedicated disposer). */
function clearActorPuppet(a: ActorEntry): void {
  if (a.puppet) {
    viewport.scene.remove(a.puppet);
    if (a.vrm) disposeVrm(a.vrm);
    else disposeObject(a.puppet);
    a.puppet = null;
  }
  a.vrm = null;
  a.avatarName = null;
  a.targetMap = null;
  a.retargeter = null; // boneOverrides survive — they describe the SOURCE rig
  a.fingerTuner = null; // fingerTune values survive for a same-style re-install
}

/** Remove retarget puppets but keep the source skeletons + their helpers. */
function clearPuppets(): void {
  for (const a of actors) clearActorPuppet(a);
  currentPuppetSource = null;
  state.viewMode = 'skeleton';
  sidebar.setPuppetAvailable(false);
  refreshActorPanel();
}

// --- Exports -------------------------------------------------------------------
const overlay = document.getElementById('export-overlay') as HTMLElement;
const overlayLabel = document.getElementById('export-label') as HTMLElement;
const overlayProgress = document.getElementById('export-progress') as HTMLProgressElement;

// Scene-file asset prompt (asks the user to supply referenced files on load).
const scenePrompt = document.getElementById('scene-prompt') as HTMLElement;
const scenePromptMsg = document.getElementById('scene-prompt-msg') as HTMLElement;
const scenePromptInput = document.getElementById('scene-prompt-input') as HTMLInputElement;
document.getElementById('scene-prompt-choose')!.addEventListener('click', () =>
  scenePromptInput.click()
);
document.getElementById('scene-prompt-cancel')!.addEventListener('click', () => {
  scenePrompt.hidden = true;
  pendingScene = null;
});
scenePromptInput.addEventListener('change', () => {
  const file = scenePromptInput.files?.[0];
  scenePromptInput.value = '';
  if (!file || !pendingScene) return;
  const req = pendingScene.queue[pendingScene.index];
  pendingScene.files[req.fileKey] = file;
  pendingScene.index++;
  scenePrompt.hidden = true;
  promptForNextAsset();
});

async function runGlbExport(): Promise<void> {
  if (state.exporting) return;
  const source = state.source;
  if (!source || state.totalFrames === 0) {
    showError('Load a mocap .fbx before exporting.');
    return;
  }

  // When a preview mesh is loaded, export the retargeted character meshes; else
  // export the raw skeleton(s). Either way the animation is baked per-bone (by
  // UUID) so every actor exports correctly, plus a camera matching the viewport.
  const puppetActors = actors.filter((a) => a.puppet && a.retargeter);
  const usePuppets = puppetActors.length > 0;

  const wasPlaying = state.playing;
  timeline.pause();
  loop.stop();
  setExporting(true, 'Exporting GLB…');
  overlayProgress.removeAttribute('value');

  const sourceBones: THREE.Object3D[] = [];
  if (!usePuppets) {
    source.traverse((c) => {
      if ((c as THREE.Bone).isBone) sourceBones.push(c);
    });
  }
  const base = (sourceFile?.name ?? 'mocap').replace(/\.[^.]+$/, '');

  try {
    await bakeAndExportGlb({
      roots: usePuppets ? puppetActors.map((a) => a.puppet!) : [source],
      retargeters: usePuppets ? puppetActors.map((a) => a.retargeter!) : [],
      sourceBones,
      startFrame: state.startFrame,
      endFrame: state.endFrame,
      fps: state.fps,
      // Optional downsample (never up-sample past the timeline rate).
      sampleFps: Math.min(state.glbExportFps ?? state.fps, state.fps),
      // Proportion matching drives every joint's position, so bake them all.
      bakeAllPositions: state.matchProportions,
      // Keyframed offsets + finger healing run with the mixers so both bake in.
      setTime: (seconds) => {
        setMixersTime(seconds);
        evalActorAdjustsAt(seconds);
        if (state.fingerFix > 0) fingerHealer?.apply(state.fingerFix);
      },
      // Finger tuning + contact-pin IK bake after the retargeters each frame.
      afterPose: (seconds) => {
        for (const a of puppetActors) {
          if (a.fingerTuner && isFingerTuneActive(a.fingerTune)) a.fingerTuner.apply(a.fingerTune);
        }
        if (state.contacts.length > 0) applyAllContacts(seconds, usePuppets);
      },
      camera: viewport.camera,
      cameraTarget: viewport.controls.target,
      // Export what you see: bake the camera program when not in Free view.
      sampleCamera:
        state.cameraView !== 'free' && state.cameras.cameras.length > 0
          ? (sec) => {
              const s = director.sample(state.cameraView, sec);
              return s
                ? { position: s.position.clone(), target: s.target.clone(), fov: s.fov }
                : null;
            }
          : undefined,
      filename: `${base}_segment.glb`,
    });
    showToast(
      usePuppets
        ? `Exported ${puppetActors.length} mesh actor(s) + camera.`
        : 'Exported skeleton(s) + camera.'
    );
  } catch (err) {
    showError((err as Error).message);
  } finally {
    // Restore the live playhead + scene.
    setMixersTime(state.currentFrame / state.fps);
    tickActors();
    loop.renderOnce();
    loop.start();
    setExporting(false);
    if (wasPlaying) timeline.play();
  }
}

async function runVideoExport(): Promise<void> {
  if (state.exporting) return;
  const wasPlaying = state.playing;
  // If an aspect ratio is chosen, render at the exact preset resolution; else use
  // the live viewport size (even-clamped inside exportVideo).
  const dims = exportDimensions(state.aspect, state.resolution);
  let restoreExport: (() => void) | null = null;
  // Compositing burns the reference video into the frames using the live
  // overlay settings. The flag must be set BEFORE setExporting so the
  // subscribe() reconciler keeps the keyed transparent background alive.
  const compositing = state.video.includeInExport && videoOverlay.hasVideo();
  videoCompositeExport = compositing;
  try {
    timeline.pause();
    loop.stop();
    setExporting(true, 'Rendering MP4…');
    overlayProgress.value = 0;

    // Output dims: the aspect preset, else the live canvas.
    const even = (n: number) => n - (n % 2);
    const outW = dims?.width ?? even(canvas.width);
    const outH = dims?.height ?? even(canvas.height);

    if (compositing) {
      // Keyed transparency must survive into the exported frames — the
      // compositor supplies the video underneath.
      viewport.setCompositeExport(true);
      // Split layouts show the 3D in one half, so render it at half size.
      if (state.video.layout === 'split-lr') {
        restoreExport = viewport.beginExport(even(Math.floor(outW / 2)), outH);
      } else if (state.video.layout === 'split-tb') {
        restoreExport = viewport.beginExport(outW, even(Math.floor(outH / 2)));
      } else if (dims) {
        restoreExport = viewport.beginExport(dims.width, dims.height);
      }
      videoOverlay.element().pause();
    } else if (dims) {
      restoreExport = viewport.beginExport(dims.width, dims.height);
    }

    await exportVideo({
      canvas,
      fps: state.fps,
      startFrame: state.startFrame,
      endFrame: state.endFrame,
      // Optional downsample (never up-sample past the timeline rate).
      exportFps: Math.min(state.videoExportFps ?? state.fps, state.fps),
      outputWidth: outW,
      outputHeight: outH,
      composite: compositing
        ? {
            video: videoOverlay.element(),
            layout: state.video.layout,
            opacity: state.video.opacity,
            sceneOpacity: state.video.sceneOpacity,
            onTop: state.video.onTop,
            frameDelta: state.video.frameDelta,
          }
        : undefined,
      stepToFrame: (frame) => {
        if (!state.mixer) return;
        setMixersTime(frame / state.fps);
        tickActors();
        // Export what you see: Program/look-through views render the camera
        // program frame-exactly (this path has no controls.update() to fight).
        if (state.cameraView !== 'free') director.applyToViewport(frame / state.fps);
        viewport.render();
      },
      onProgress: (done, total) => {
        overlayProgress.max = total;
        overlayProgress.value = done;
        overlayLabel.textContent = `Rendering MP4… ${done}/${total}`;
      },
    });
    showToast('MP4 exported.');
  } catch (err) {
    showError((err as Error).message);
  } finally {
    videoCompositeExport = false;
    viewport.setCompositeExport(false);
    restoreExport?.();
    setExporting(false);
    // Restore the viewport to the current frame and resume the loop.
    if (state.mixer) setMixersTime(state.currentFrame / state.fps);
    loop.renderOnce();
    loop.start();
    if (wasPlaying) timeline.play();
  }
}

// --- Scene file (save settings, reference assets by filename) -------------------
function saveScene(): void {
  if (!sourceFile) {
    showError('Load a mocap .fbx before saving a scene.');
    return;
  }
  const cam = viewport.camera;
  let puppet: ScenePuppet = null;
  if (currentPuppetSource?.type === 'baked') {
    puppet = { type: 'baked', id: currentPuppetSource.id };
  } else if (currentPuppetSource?.type === 'custom') {
    puppet = { type: 'custom', name: currentPuppetSource.name, ext: currentPuppetSource.ext };
  }

  const scene: SceneFile = {
    version: 2,
    app: 'aka-mocap-viewer',
    camera: {
      position: cam.position.toArray(),
      target: viewport.controls.target.toArray(),
      fov: cam.fov,
      up: cam.up.toArray(),
    },
    cameras: state.cameras,
    cameraView: state.cameraView,
    background: state.background,
    aspect: state.aspect,
    resolution: state.resolution,
    fps: state.fps,
    timeUnit: state.timeUnit,
    viewMode: state.viewMode,
    marks: { start: state.startFrame, end: state.endFrame, current: state.currentFrame },
    actors: actors.map((a) => ({
      id: a.item.id,
      label: a.item.label,
      kind: a.item.kind,
      visible: a.visible,
      color: a.color,
      adjust: { offset: [...a.adjust.offset], scale: a.adjust.scale },
      adjustKeys: a.adjustKeys.map((k) => ({
        time: k.time,
        offset: [...k.offset],
        scale: k.scale,
      })),
      fingerTune: { ...a.fingerTune },
      fingerTuneKeys: a.fingerTuneKeys.map((k) => ({ time: k.time, tune: { ...k.tune } })),
      avatar: a.avatarName,
      boneMap: a.boneOverrides ? Object.fromEntries(a.boneOverrides) : null,
    })),
    source: { name: sourceFile.name, ext: sourceFile.ext },
    puppet,
    video: state.video.name ? { ...state.video } : null,
    contacts: state.contacts,
    fingerFix: state.fingerFix,
    lighting: { ...state.lighting },
    matchProportions: state.matchProportions,
  };

  const blob = new Blob([JSON.stringify(scene, null, 2)], { type: 'application/json' });
  const base = sourceFile.name.replace(/\.[^.]+$/, '');
  downloadBlob(blob, `${base}.scene.json`);
  showToast('Scene saved.');
}

/** A file the scene references and must be re-supplied on load. */
type AssetKind = 'source' | 'puppet' | 'video' | 'avatar';
interface RequiredAsset {
  kind: AssetKind;
  name: string;
  accept: string;
  /** Unique key into the collected files ('source', 'avatar:<actorId>', …). */
  fileKey: string;
  /** For avatar assets: which actor wears it (used in the prompt message). */
  actorLabel?: string;
}
let pendingScene: {
  scene: SceneFile;
  queue: RequiredAsset[];
  index: number;
  files: Record<string, File>;
} | null = null;

function loadScene(file: File): void {
  file
    .text()
    .then((text) => {
      const scene = JSON.parse(text) as SceneFile;
      if (scene.app !== 'aka-mocap-viewer' || !scene.source) {
        throw new Error('Not a valid AKA scene file.');
      }
      const queue: RequiredAsset[] = [
        { kind: 'source', name: scene.source.name, accept: '.fbx', fileKey: 'source' },
      ];
      if (scene.puppet?.type === 'custom') {
        queue.push({
          kind: 'puppet',
          name: scene.puppet.name,
          accept: '.glb,.gltf,.fbx',
          fileKey: 'puppet',
        });
      }
      for (const sa of scene.actors ?? []) {
        if (sa.avatar) {
          queue.push({
            kind: 'avatar',
            name: sa.avatar,
            accept: '.vrm',
            fileKey: `avatar:${sa.id}`,
            actorLabel: sa.label,
          });
        }
      }
      if (scene.video?.name) {
        queue.push({
          kind: 'video',
          name: scene.video.name,
          accept: '.mp4,.mov,.m4v,.webm',
          fileKey: 'video',
        });
      }
      pendingScene = { scene, queue, index: 0, files: {} };
      promptForNextAsset();
    })
    .catch((err) => showError(`Couldn't read scene: ${(err as Error).message}`));
}

/** Show the prompt for the next referenced file, or apply the scene if all gathered. */
function promptForNextAsset(): void {
  if (!pendingScene) return;
  if (pendingScene.index >= pendingScene.queue.length) {
    const scene = pendingScene.scene;
    const files = pendingScene.files;
    pendingScene = null;
    void applyScene(scene, files);
    return;
  }
  const req = pendingScene.queue[pendingScene.index];
  const what =
    req.kind === 'source'
      ? 'mocap take'
      : req.kind === 'puppet'
        ? 'character mesh'
        : req.kind === 'avatar'
          ? `VRM avatar for ${req.actorLabel ?? 'an actor'}`
          : 'reference video';
  scenePromptMsg.textContent = `This scene needs the ${what} file: "${req.name}". Choose it to continue.`;
  scenePromptInput.accept = req.accept;
  scenePrompt.hidden = false;
}

/** Apply a scene once every referenced asset has been provided. */
async function applyScene(scene: SceneFile, files: Record<string, File>): Promise<void> {
  try {
    setExporting(true, 'Loading scene…');
    overlayProgress.removeAttribute('value');

    const sourceBuffer = await files.source!.arrayBuffer();
    installMocap(parseMocapFbx(sourceBuffer), scene.source!.name);
    sourceFile = { name: scene.source!.name, ext: scene.source!.ext, buffer: sourceBuffer };

    if (scene.fps) timeline.setFps(scene.fps);

    // Before puppets/avatars install — their retargeters read this flag.
    state.matchProportions = scene.matchProportions ?? false;

    if (scene.puppet?.type === 'baked') {
      await loadBakedPuppet(scene.puppet.id);
    } else if (scene.puppet?.type === 'custom' && files.puppet) {
      const puppetBuffer = await files.puppet.arrayBuffer();
      const { object, bones } = await parseTargetMesh(puppetBuffer, scene.puppet.ext);
      installPuppet(object, bones, scene.puppet.name, {
        type: 'custom',
        name: scene.puppet.name,
        ext: scene.puppet.ext,
        buffer: puppetBuffer,
      });
    }

    for (const sa of scene.actors ?? []) {
      const a = actors.find((x) => x.item.id === sa.id);
      if (!a) continue;
      if (sa.color !== null) a.color = sa.color;
      a.visible = sa.visible;
      if (sa.adjust) {
        // Mutate in place — the retargeter holds a live reference.
        a.adjust.offset[0] = sa.adjust.offset[0] ?? 0;
        a.adjust.offset[1] = sa.adjust.offset[1] ?? 0;
        a.adjust.offset[2] = sa.adjust.offset[2] ?? 0;
        a.adjust.scale = sa.adjust.scale ?? 1;
        applyActorAdjust(a);
      }
      a.adjustKeys = (sa.adjustKeys ?? [])
        .filter((k) => typeof k?.time === 'number' && Array.isArray(k.offset))
        .map((k) => ({
          time: Math.max(0, k.time),
          offset: [k.offset[0] ?? 0, k.offset[1] ?? 0, k.offset[2] ?? 0] as [
            number,
            number,
            number,
          ],
          scale: typeof k.scale === 'number' ? k.scale : 1,
        }))
        .sort((x, y) => x.time - y.time);
      // Restore finger tuning BEFORE avatars load (the VRM0 auto-fix defers to it).
      if (sa.fingerTune) a.fingerTune = sanitizeFingerTune(sa.fingerTune);
      a.fingerTuneKeys = (sa.fingerTuneKeys ?? [])
        .filter((k) => typeof k?.time === 'number')
        .map((k) => ({ time: Math.max(0, k.time), tune: sanitizeFingerTune(k.tune) }))
        .sort((x, y) => x.time - y.time);
      // Restore bone remaps BEFORE avatars load so retargeters honour them.
      a.boneOverrides = sa.boneMap ? new Map(Object.entries(sa.boneMap)) : null;
      applyActorColor(a);
    }
    refreshActorPanel();

    // Per-actor VRM avatars (the prompt queue collected one file per actor).
    for (const sa of scene.actors ?? []) {
      const file = files[`avatar:${sa.id}`];
      if (!file || !sa.avatar) continue;
      const buffer = await file.arrayBuffer();
      await assignVrmToActor(sa.id, buffer, sa.avatar);
    }

    // Contact pins + finger healing.
    state.contacts = sanitizeContacts(scene.contacts ?? []);
    state.selectedContactId = null;
    state.fingerFix = Math.min(Math.max(scene.fingerFix ?? 0, 0), 1);

    setBackground(scene.background);
    setAspect(scene.aspect);
    setResolution(scene.resolution);
    if (scene.viewMode) setViewMode(scene.viewMode);
    if (scene.timeUnit) timeline.setTimeUnit(scene.timeUnit);
    if (scene.lighting) {
      state.lighting = { ...scene.lighting };
      // Older scene files predate shadowOpacity.
      state.lighting.shadowOpacity =
        (scene.lighting.shadowOpacity as number | undefined) ?? 0.35;
      environment.setGroundShadow(state.lighting.shadow);
      environment.setShadowOpacity(state.lighting.shadowOpacity);
      environment.setLightAngles(state.lighting.azimuth, state.lighting.elevation);
    }

    // Reference video: restore its settings, then load the supplied file.
    if (scene.video) {
      state.video = { ...scene.video };
      // Older scene files predate keyBackground / includeInExport.
      state.video.keyBackground = (scene.video.keyBackground as boolean | undefined) ?? true;
      state.video.includeInExport =
        (scene.video.includeInExport as boolean | undefined) ?? false;
      if (files.video) loadVideoFile(files.video);
    }

    if (scene.marks) {
      timeline.markStart(scene.marks.start);
      timeline.markEnd(scene.marks.end);
      timeline.scrubToFrame(scene.marks.current);
    }

    // Leave any camera view first (so its snapshot can't clobber the saved
    // free pose), apply the saved free camera, then the program + saved view.
    director.setView('free');
    if (scene.camera) camRestore(scene);
    if (scene.cameras) {
      const program = sanitizeProgram(scene.cameras);
      if (program) {
        state.cameras = program;
        state.selectedCameraId = program.cameras[0]?.id ?? null;
        state.selectedKeyframe = null;
      }
    }
    if (scene.cameraView && scene.cameraView !== 'free') {
      director.setView(scene.cameraView);
    }

    applyActorVisibility();
    loop.renderOnce();
    notify();
    showToast('Scene loaded.');
  } catch (err) {
    showError(`Couldn't load scene: ${(err as Error).message}`);
  } finally {
    setExporting(false);
  }
}

function camRestore(scene: SceneFile): void {
  const cam = viewport.camera;
  cam.position.fromArray(scene.camera.position);
  cam.up.fromArray(scene.camera.up);
  cam.fov = scene.camera.fov;
  cam.updateProjectionMatrix();
  viewport.controls.target.fromArray(scene.camera.target);
  viewport.controls.update();
  updateCameraReadout();
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function setExporting(on: boolean, label = 'Exporting…'): void {
  state.exporting = on;
  overlay.hidden = !on;
  overlayLabel.textContent = label;
  notify();
}

// --- Agent tooling API (docs/AUTO_CLEANUP.md) ------------------------------------

function buildAgentSnapshot(): SceneSnapshot {
  return {
    take: {
      name: sourceFile?.name ?? null,
      fps: state.fps,
      totalFrames: state.totalFrames,
      durationSeconds: +clipDurationSeconds().toFixed(3),
      marks: { start: state.startFrame, end: state.endFrame },
      playhead: state.currentFrame,
      viewMode: state.viewMode,
    },
    actors: actors.map((a) => ({
      id: a.item.id,
      label: a.item.label,
      kind: a.item.kind,
      visible: a.visible,
      avatar: a.avatarName,
      hasPuppet: a.puppet !== null,
      adjust: { offset: [...a.adjust.offset], scale: a.adjust.scale },
      adjustKeys: a.adjustKeys.map((k) => ({ time: k.time, offset: [...k.offset], scale: k.scale })),
      fingerTune: { ...a.fingerTune },
      fingerTuneKeys: a.fingerTuneKeys.map((k) => ({ time: k.time, tune: { ...k.tune } })),
    })),
    contacts: JSON.parse(JSON.stringify(state.contacts)) as typeof state.contacts,
    fingerFix: state.fingerFix,
    cameras: {
      count: state.cameras.cameras.length,
      shots: state.cameras.shots.length,
      view: state.cameraView,
    },
  };
}

/** Everything adjustable, JSON-cloned — the agent's checkpoint payload. */
interface AgentSettings {
  fingerFix: number;
  contacts: ContactPin[];
  cameras: unknown;
  marks: { start: number; end: number };
  actors: {
    id: string;
    visible: boolean;
    color: number | null;
    adjust: { offset: number[]; scale: number };
    adjustKeys: AdjustKey[];
    fingerTune: FingerTune;
    fingerTuneKeys: FingerTuneKey[];
    boneMap: Record<string, string> | null;
  }[];
}

function captureAgentSettings(): AgentSettings {
  return JSON.parse(
    JSON.stringify({
      fingerFix: state.fingerFix,
      contacts: state.contacts,
      cameras: state.cameras,
      marks: { start: state.startFrame, end: state.endFrame },
      actors: actors.map((a) => ({
        id: a.item.id,
        visible: a.visible,
        color: a.color,
        adjust: a.adjust,
        adjustKeys: a.adjustKeys,
        fingerTune: a.fingerTune,
        fingerTuneKeys: a.fingerTuneKeys,
        boneMap: a.boneOverrides ? Object.fromEntries(a.boneOverrides) : null,
      })),
    })
  ) as AgentSettings;
}

function restoreAgentSettings(data: unknown): void {
  const d = data as AgentSettings;
  state.fingerFix = d.fingerFix ?? 0;
  state.contacts = sanitizeContacts(d.contacts ?? []);
  state.selectedContactId = null;
  const program = sanitizeProgram(d.cameras);
  if (program) state.cameras = program;
  if (d.marks) {
    timeline.markStart(d.marks.start);
    timeline.markEnd(d.marks.end);
  }
  for (const sa of d.actors ?? []) {
    const a = actors.find((x) => x.item.id === sa.id);
    if (!a) continue;
    a.visible = sa.visible;
    a.color = sa.color;
    // Mutate adjust in place — the retargeter holds a live reference.
    a.adjust.offset[0] = sa.adjust.offset[0] ?? 0;
    a.adjust.offset[1] = sa.adjust.offset[1] ?? 0;
    a.adjust.offset[2] = sa.adjust.offset[2] ?? 0;
    a.adjust.scale = sa.adjust.scale ?? 1;
    a.adjustKeys = sa.adjustKeys ?? [];
    a.fingerTune = sanitizeFingerTune(sa.fingerTune);
    a.fingerTuneKeys = (sa.fingerTuneKeys ?? []).map((k) => ({
      time: k.time,
      tune: sanitizeFingerTune(k.tune),
    }));
    const boneMap = sa.boneMap ? new Map(Object.entries(sa.boneMap)) : null;
    const boneMapChanged =
      JSON.stringify(sa.boneMap ?? null) !==
      JSON.stringify(a.boneOverrides ? Object.fromEntries(a.boneOverrides) : null);
    a.boneOverrides = boneMap;
    if (boneMapChanged && a.puppet) rebuildActorRetargeter(a);
    applyActorAdjust(a);
    applyActorColor(a);
  }
  applyActorVisibility();
  refreshActorPanel();
  tickActors();
  loop.renderOnce();
  notify();
}

const analyzer = createAnalyzer({
  loop,
  setMixersTime,
  getSkeletonActors: () =>
    actors
      .filter((a) => a.item.kind === 'skeleton')
      .map((a) => ({ id: a.item.id, label: a.item.label })),
  sourceBone: (actorId, canonical) => {
    const a = actors.find((x) => x.item.id === actorId);
    return a ? sourceBoneOf(a, canonical) : null;
  },
  getFingerHealer: () => fingerHealer,
});

const agentApi = createAgentApi({
  viewport,
  loop,
  director,
  analyze: (opts) => analyzer.analyze(opts),
  setMixersTime,
  tickActors,
  actorBone: (actorId, canonical) =>
    actorBoneObj(actorId, canonical, state.viewMode === 'mesh'),
  buildSnapshot: buildAgentSnapshot,
  captureSettings: captureAgentSettings,
  restoreSettings: restoreAgentSettings,
  scrub: (frame) => timeline.scrubToFrame(frame),
  setMarks: (start, end) => {
    timeline.markStart(start);
    timeline.markEnd(end);
  },
  setFingerTune: (actorId, patch) => setFingerTune(actorId, patch),
  keyFingerTune: (actorId, seconds) => {
    const a = actors.find((x) => x.item.id === actorId);
    if (a) {
      upsertFingerTuneKey(a, false, seconds);
      notify();
    }
  },
  deleteFingerTuneKey: (actorId, index) => deleteFingerTuneKey(actorId, index),
  setActorAdjust: (actorId, patch) => setActorAdjust(actorId, patch),
  keyActorOffset: (actorId, seconds) => {
    const a = actors.find((x) => x.item.id === actorId);
    if (a) {
      upsertAdjustKey(a, false, seconds);
      notify();
    }
  },
  deleteAdjustKey: (actorId, index) => deleteAdjustKey(actorId, index),
  setFingerFix: (strength) => {
    state.fingerFix = strength;
    tickActors();
    loop.renderOnce();
    notify();
  },
  addPin: (spec) => {
    const pin: ContactPin = sanitizeContacts([
      {
        id: crypto.randomUUID(),
        actorId: spec.actorId,
        limb: spec.limb,
        target: spec.target,
        keys: spec.keys ?? [{ time: frameToSeconds(state.currentFrame), value: 1 }],
        enabled: spec.enabled ?? true,
      },
    ])[0];
    if (!pin) throw new Error('Invalid pin spec.');
    state.contacts.push(pin);
    state.selectedContactId = pin.id;
    notify();
    return pin.id;
  },
  updatePin: (pinId, patch) => updateContactPin(pinId, patch as Parameters<typeof updateContactPin>[1]),
  setPinKey: (pinId, seconds, value) => setContactKey(pinId, value, seconds),
  deletePin: (pinId) => deleteContactPin(pinId),
});

const assistant = createAssistant(
  agentApi,
  () => localStorage.getItem('aka-assistant-key') ?? ''
);
const assistantPanel = createAssistantPanel(assistant, () => state.mixers.length > 0);

// --- Toast / errors ------------------------------------------------------------
const toast = document.getElementById('toast') as HTMLElement;
let toastTimer = 0;

function showToast(message: string, isError = false): void {
  toast.textContent = message;
  toast.classList.toggle('error', isError);
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toast.hidden = true), isError ? 6000 : 3500);
}

function showError(message: string): void {
  showToast(message, true);
  console.error(message);
}

// --- Go ------------------------------------------------------------------------
loop.start();
notify();

// Dev-only inspection hook for debugging / automated checks.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__viewer = {
    scene: viewport.scene,
    renderer: viewport.renderer,
    camera: viewport.camera,
    controls: viewport.controls,
    state,
    director,
  };
  // Agent tooling API (docs/AUTO_CLEANUP.md) — the look/judge/adjust surface.
  (window as unknown as Record<string, unknown>).aka = agentApi;
}
